// The room's material, as a shelf you build from — now a browser.
//
// Everything a Set is made of, once each (the timeline's clips and the Session
// View's, grouped by what makes them SOUND different — `buildRoomClipLibrary`),
// plus the producer's own clip library, plus what was used lately, through one
// list of `MaterialItem` rows (web-shared/lib/material-item.ts). The rows do
// not know where they came from; the ＋, the drag, the preview and the chips
// read the row.
//
// Borrowed from Live's Browser (docs/sdk/live-browser.md), sized for a thumb:
//   Places     Project · Mine · Recent — a short list of WHERE, not a tree
//   Folders    under Mine only (Project's folders are its tracks): a path
//              line and folder rows in the same list, drilled into the way
//              Note's browser is; a search looks through the folder and
//              everything under it, and a row from a subfolder says which.
//              Rows move by a drag onto a folder row or the Move to… picker
//   Tags       Live's Quick Tags, as a footer under the list: the active row's
//              confirmed tags (× removes), the browser's suggestions dim with
//              a ✓, and Add… completing over the fixed groups and the words
//              already in use. ⇧/⌘-click selects several rows (a mouse);
//              the footer then tags them all
//   By tag     Live's filter view and its labels, two ways: a Tags section at
//              Mine's root that drills Group › Value › clips like folders do,
//              and a filter view (≡) under the chips listing every group's
//              values with counts. Values of one group OR, groups AND. A
//              row's own tags are pills you can tap
//   Smart      Live's "Add Label": a search and its chips kept as a folder
//   folders    at Mine's root, live — what Clear would show if you typed it
//              again. Opened like a folder, edited by changing the search
//   Chips      Loops · One-shots · MIDI · ★ · a chip per track — AND between
//              families, OR within one; a `#tag` in the search box is a chip
//              you typed
//   Search     every word must match (AND); × clears the text, Clear clears
//              everything; Esc in the box does what Clear does
//   Preview    tap the art: loops start on the next bar while the transport
//              runs and keep looping; Raw plays them dry, from the top, once
//   Two ways in  drag a row (a mouse: HTML5 drag; a finger: hold 450 ms, the
//              row lifts, carry it — lib/lift-drag.ts), or ＋ to place it where
//              the row says it will land
//   History    none yet (Live's ‹ › is a desktop habit); Recent covers the
//              phone's version of it
//
// This is not the Pads screen and does not launch anything. Presentation
// only: RoomPage decides what a drop or a ＋ means.

import {
  useCallback,
  useEffect,
  useImperativeHandle,
  Fragment,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type Ref,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { ArrowDownUp, ArrowLeftRight, Bookmark, BookmarkPlus, Check, ChevronLeft, ChevronRight, Download, Folder, FolderInput, FolderPlus, Hash, ListChecks, ListFilter, Loader2, Music, Pause, Pencil, Play, Plus, Save, Search, SkipForward, Sparkles, Star, Tag, Tags, Trash2, Upload, X } from "lucide-react";
import type { ClipUse, ClipUsesResponse, CreateSmartFolderRequest, MyClip, MyFolder, MyMidi, MySmartFolder, ReferenceSeed, TagDelta, UpdateClipRequest, UpdateSmartFolderRequest } from "@shared/api-types.js";
import { poolHashAnyChannel } from "@shared/blobs.js";
import { Link } from "react-router";
import { createPortal } from "react-dom";
import { useIsWide } from "@web-shared/hooks/useIsNarrow";
import { seedPath } from "@/lib/ctas";
import { useHold } from "@web-shared/hooks/useHold";
import { usePref } from "@web-shared/hooks/usePref";
import { drawEnvelope, prepareCanvas } from "@web-shared/lib/waveform";
import { track } from "@web-shared/lib/analytics";
import { objectUrl } from "@web-shared/lib/api";
import { clipColor } from "@web-shared/lib/arrangement";
import type { ClipSource, MidiSource } from "@web-shared/lib/audio-engine";
import { MATERIAL_DRAG_TYPE, MINE_DRAG_TYPE, MINE_FOLDER_DRAG_TYPE, MINE_MIDI_DRAG_TYPE } from "@web-shared/lib/drag-types";
import { LIBRARY_FOLDER_MAX_DEPTH } from "@shared/api-types.js";
import { clipStatusText, type ClipFileView } from "@/lib/web-upload/clip-progress";
import { overallProgress, secondsLeft, timeLeftText, type ProgressStage } from "@/lib/web-upload/progress";
import { normalizeTag, parseTag, sortTags, tagAnswersTo, tagIndexOf, tagLabel, validTag, type TagIndexGroup } from "@shared/library-tags.js";
import { SUGGESTED_CHIP, UNREVIEWED_CHIP, UNUSED_CHIP } from "@shared/library-search.js";
import { startLift } from "@web-shared/lib/lift-drag";
import {
  canMoveFolder,
  childFolders,
  completeTag,
  completeVocabulary,
  defaultSort,
  folderBytes,
  folderCounts,
  folderPath,
  folderSubtree,
  itemFromMyClip,
  itemFromMyMidi,
  itemsFromLibrary,
  itemsFromReference,
  parseReference,
  referenceMidiOf,
  lengthLabel,
  matchedSuggestions,
  matches,
  parseQuery,
  SORT_LABELS,
  sortItems,
  suggestTagFor,
  type MaterialChip,
  type MaterialItem,
  type MaterialPayload,
  type MaterialPlace,
  type MaterialSort,
  type TagCompletion,
} from "@web-shared/lib/material-item";
import { loadFavorites, loadRecent, saveFavorites, saveRecent, touchRecent } from "@web-shared/lib/recent-material";
import { cleanClipName, cleanTrackName } from "@/lib/track-name";
import { relativeTime } from "@web-shared/lib/format";
import { cn } from "@web-shared/lib/utils";
import type { ClipLibrary } from "@web-shared/lib/clip-library";
import type { ArrangementPlayerApi } from "@web-shared/hooks/useArrangementPlayer";

/** Which place the shelf shows. Kept under its old name for the hook that owns it. */
export type MaterialFilter = MaterialPlace;

/** What the shelf needs to show and act on the producer's own library. */
export interface MineShelf {
  signedIn: boolean;
  clips: MyClip[];
  /** MIDI ideas kept in the library. */
  midi: MyMidi[];
  /** The folder tree (flat; `parentId` links it). */
  folders: MyFolder[];
  /** Saved searches, shown as folders at the root. */
  smartFolders: MySmartFolder[];
  onCreateSmartFolder: (req: CreateSmartFolderRequest) => Promise<MySmartFolder | null>;
  onUpdateSmartFolder: (id: string, req: UpdateSmartFolderRequest) => void;
  onDeleteSmartFolder: (id: string) => void;
  usedBytes: number;
  quotaBytes: number;
  loading: boolean;
  /** Why the library could not be loaded — shown in place of the rows, never
   * as an empty library: "nothing here" and "could not ask" must not look
   * the same. */
  error?: string | null;
  onRetry?: () => void;
  onSignIn: () => void;
  /** Place this clip at the playhead (the selected lane, else a new one) —
   * or, on the Pads view, in a pad (`placeTarget`). */
  onPlace: (clip: MyClip) => void;
  placeTarget?: "timeline" | "pads";
  /** Place a MIDI idea at the playhead (the selected MIDI lane, else a new one). */
  onPlaceMidi: (midi: MyMidi) => void;
  onDelete: (id: string) => void;
  onDeleteMidi: (id: string) => void;
  /** Keep one of the room's MIDI clips in the library, in a folder. */
  onSaveMidi: (materialClipId: string, folderId: string | null) => void;
  /** Keep one of the room's audio clips in the library. Absent ⇒ no Save on audio. */
  onSaveAudio?: (materialClipId: string, folderId: string | null) => void;
  /** The clips of every seed the producer has been in: the Seeds place. */
  seeds: ReferenceSeed[];
  /** A seed's clip, kept into Mine (the folder on screen). */
  onKeepReference: (seedId: string, audioKey: string, folderId: string | null) => void;
  /** ＋ on a seed's clip: into Mine, then at the playhead. */
  onPlaceReference: (seedId: string, audioKey: string) => void;
  /** The audio clip open in the editor, when one is: ⇄ on a library row
   * swaps its sound in, keeping the clip's place on the timeline. */
  swapTarget?: { clipId: string; name: string } | null;
  onSwap?: (clip: MyClip) => void;
  /** Star or unstar a library row (travels with the account). */
  onStar: (payload: MaterialPayload, starred: boolean) => void;
  /** A row's name, changed in place (double-click it). */
  onRename: (payload: MaterialPayload, name: string) => void;
  /** Live's Collections: one colour on one or many rows (null clears). */
  onColor: (payloads: readonly MaterialPayload[], value: number | null) => void;
  /** A library row was previewed or placed: Recent follows the account. */
  onTouch: (key: string) => void;
  /** Recent keys the account remembers, newest first — other devices' too. */
  recent: readonly string[];
  /** The seeds a clip has been placed in. */
  usesOf: (id: string) => Promise<ClipUsesResponse>;
  /** Drop everything one upload added. */
  onUndoBatch: (batchId: string) => void;
  /** Confirm / add (`set`) or remove / reject (`unset`) tags on one row. */
  onTag: (payload: MaterialPayload, delta: TagDelta) => void;
  /** The same delta on several rows at once. */
  onTagMany: (payloads: readonly MaterialPayload[], delta: TagDelta) => void;
  /** One of your free tags renamed everywhere — or merged into the tag named. */
  onRenameTag: (from: string, to: string) => void;
  /** The waiting suggestions of several rows, kept or dismissed at once —
   * every one, or only the named tags. */
  onReview: (payloads: readonly MaterialPayload[], keep: boolean, tags?: readonly string[]) => void;
  /** Tags written as suggestions (source folder) on several rows. */
  onSuggest: (payloads: readonly MaterialPayload[], tags: readonly string[]) => void;
  /** What a folder's name (and its ancestors') say, as tags; empty when nothing. */
  folderTags: (folderId: string) => string[];
  /** An edit to a clip's own fields — accepting a heard tempo sets `bpm`. */
  onUpdateClip: (id: string, req: UpdateClipRequest) => void;
  /** Move library rows into a folder (null = the root). */
  onMove: (items: readonly MaterialPayload[], folderId: string | null) => void;
  onCreateFolder: (name: string, parentId: string | null) => Promise<MyFolder | null>;
  onRenameFolder: (id: string, name: string) => void;
  /** Move a folder, and everything in it, under another (null = the root). */
  onMoveFolder: (id: string, parentId: string | null) => void;
  /** Delete a folder; what it held steps up to its parent. */
  onDeleteFolder: (id: string) => void;
  /** Save this clip's file to disk. Absent ⇒ no download button. */
  onDownload?: (clip: MyClip) => void;
  /** Save several clips as one zip, named — a smart folder's rows. Absent ⇒ no button. */
  onDownloadMany?: (clips: MyClip[], name: string) => void;
  /** The id being deleted, while it is. */
  removing: string | null;
  /** Audio files handed to the shelf: add them to the library, into a folder. */
  onAddFiles: (dt: DataTransfer, folderId: string | null) => void;
  /** The same, from a file picker (the library page's Add files). */
  onAddPicked: (list: FileList | File[], folderId: string | null) => void;
  /** A status line while files are being added, or the reason they weren't. */
  /** A status line while files are being added, the reason they weren't, or
   * what just landed (`done`). */
  adding: {
    text: string;
    error: boolean;
    done?: boolean;
    batch?: { id: string; count: number } | null;
    /** A row per file while an upload is in flight, with the bytes and the clock. */
    progress?: UploadProgressView;
    /** Send again what failed (or ask /complete again); absent when there is nothing to retry. */
    onRetry?: () => void;
  } | null;
  usage: string | null;
}

/** The id the search box carries, so the room's ⌘F can find it. */
export const MATERIAL_SEARCH_ID = "material-search";

/** Where the shelf is, in Mine: a folder, a smart folder, or the tag browse. */
export interface LibraryWhere {
  folderId: string | null;
  smartId: string | null;
  /** Browsing tags: the group open (null at the groups list); undefined when not. */
  tagGroup: string | null | undefined;
  /** The Needs review queue is open (the `unreviewed` chip is on). */
  review: boolean;
  /** The search box's words and the chips on, for the page's URL. */
  query: string;
  chips: string[];
}

/** What a page around the shelf can tell it to show (the library page's sidebar). */
export interface MaterialLibraryHandle {
  openFolder: (id: string | null) => void;
  openSmart: (sf: MySmartFolder) => void;
  browseTags: (group: string | null, tag?: string | null) => void;
  /** The Needs review queue: every row with a suggestion still waiting. */
  openReview: () => void;
  /** Files from the page's picker, into the folder on screen. */
  addPicked: (list: FileList | File[]) => void;
  /** Words and chips into the search, as a URL remembered them. */
  setSearch: (query: string, chips: readonly string[]) => void;
  /** Name the search on screen as a smart folder. */
  saveSearch: () => void;
}

export function MaterialLibrary({
  library,
  player,
  onPlace,
  filter,
  onFilter,
  mine,
  target = "timeline",
  onDownload,
  canDownload,
  tempo,
  roomId,
  describeTarget,
  onLifting,
  layout = "shelf",
  handleRef,
  onWhere,
  inspectorTarget = null,
  raw: rawProp,
  playOn: playOnProp,
}: {
  library: ClipLibrary;
  player: ArrangementPlayerApi;
  /** Place a copy of this material on its own track at the playhead. Absent
   * ⇒ the shelf is drag-only. */
  onPlace?: (clipId: string) => void;
  filter: MaterialPlace;
  onFilter: (f: MaterialPlace) => void;
  mine: MineShelf;
  /** Where a drag and the ＋ land: the timeline, or the pads. */
  target?: "timeline" | "pads";
  /** Download this material (the page asks which form). Absent ⇒ no button. */
  onDownload?: (clipId: string) => void;
  /** Whether a placement has audio to download (MIDI and silent ones do not). */
  canDownload?: (clipId: string) => boolean;
  /** The room's tempo: a library loop's length in bars. */
  tempo: number;
  /** What Recent and ★ are remembered under. */
  roomId: string;
  /** Where ＋ would put this row right now — "Keys @ 9.1", "a new track",
   * "pad 3 on Kick". Shown on the row so the tap is not a guess. */
  describeTarget?: (payload: MaterialPayload) => string | null;
  /** A row is being carried by a finger (true) or was let go (false). The
   * phone's sheet drops to its peek while one is. */
  onLifting?: (lifting: boolean) => void;
  /** `page`: the library page's content pane — Mine only, no place switch,
   * nothing to place into; WHERE is driven from outside through `handleRef`
   * and reported back through `onWhere`. */
  layout?: "shelf" | "page";
  handleRef?: Ref<MaterialLibraryHandle>;
  onWhere?: (where: LibraryWhere) => void;
  /** The page's inspector column: on a wide window the selection's editor
   * (waveform, facts, actions, colour, tags) is drawn there, always on, the
   * way Live's Quick Tags panel, Komplete Kontrol's Info pane and Loopcloud's
   * Properties tab sit beside their lists — instead of a footer that only
   * appears once something is picked. */
  inspectorTarget?: HTMLElement | null;
  /** Raw and Play on, owned by the host (the library page's Preview menu):
   * given, the shelf's own switches for them are not drawn. */
  raw?: boolean;
  playOn?: boolean;
}) {
  const page = layout === "page";
  const wide = useIsWide();
  const inspect = page && wide && inspectorTarget !== null;
  const [query, setQuery] = useState("");
  const [chips, setChips] = useState<ReadonlySet<MaterialChip>>(() => new Set());
  const [sort, setSort] = useState<MaterialSort | null>(null);
  const [rawOwn, setRaw] = useState(false);
  const raw = rawProp ?? rawOwn;
  const previewOutside = rawProp !== undefined || playOnProp !== undefined;
  // Play on: a preview that ends starts the next row's, down the list — a
  // pack heard end to end without a click per clip. A loop then plays one
  // pass, not forever.
  const [playOnOwn, setPlayOn] = usePref<boolean>("material:playOn", false, (v): v is boolean => typeof v === "boolean");
  const playOn = playOnProp ?? playOnOwn;
  /** The row previewed last from here, with when and for how long: an
   * audition that ends after its length ended by itself. */
  const lastAudition = useRef<{ key: string; startedAt: number; seconds: number } | null>(null);
  const auditioningRef = useRef<string | null>(null);
  auditioningRef.current = player.auditioningId;
  const shownRef = useRef<MaterialItem[]>([]);
  const [fileOver, setFileOver] = useState(false);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  // Mine's WHERE: the folder on screen, a new folder being named, a folder
  // being renamed, and rows waiting on the Move to… picker.
  const [folderId, setFolderId] = useState<string | null>(null);
  const [naming, setNaming] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [moving, setMoving] = useState<MaterialPayload[] | null>(null);
  /** A whole folder on its way to the picker. */
  const [movingFolder, setMovingFolder] = useState<MyFolder | null>(null);
  /** The folder row being dragged (a dragover cannot read the data). */
  const dragFolder = useRef<string | null>(null);
  const [dropFolder, setDropFolder] = useState<string | null>(null);
  // A mouse can hold several rows (⇧ a range, ⌘ one more); the Quick Tags
  // footer and Move to… then act on all of them.
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  // A finger has no ⌘: Select turns the rows into checkboxes until Done.
  const [selecting, setSelecting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // `#` completions show only while the search box has the keyboard: a smart
  // folder loads its words into the box without opening a list over the rows.
  const [searchFocused, setSearchFocused] = useState(false);
  // Browsing by tag: null = not; { group: null } = the groups; { group } =
  // that group's values. Picking a value sets its chip and keeps the path.
  const [tagBrowse, setTagBrowse] = useState<{ group: string | null } | null>(null);
  // Live's filter view: every group's values under the chips, with counts.
  const [filterView, setFilterView] = useState(false);
  // The smart folder open, if one is: its search is loaded into the box and
  // the chips, and stays editable; Update writes the change back.
  const [smartId, setSmartId] = useState<string | null>(null);
  const [savingSmart, setSavingSmart] = useState(false);
  const [renamingSmart, setRenamingSmart] = useState<string | null>(null);
  const [renamingTag, setRenamingTag] = useState<string | null>(null);
  useEffect(() => {
    if (smartId !== null && !mine.smartFolders.some((s) => s.id === smartId)) setSmartId(null);
  }, [smartId, mine.smartFolders]);
  // A folder that went away (deleted here or elsewhere) drops the view to the root.
  useEffect(() => {
    if (folderId !== null && !mine.folders.some((f) => f.id === folderId)) setFolderId(null);
  }, [folderId, mine.folders]);

  // Per-room memory: what was used lately, what is starred.
  const [recent, setRecent] = useState<string[]>(() => loadRecent(roomId));
  const [favorites, setFavorites] = useState<ReadonlySet<string>>(() => loadFavorites(roomId));
  useEffect(() => {
    setRecent(loadRecent(roomId));
    setFavorites(loadFavorites(roomId));
  }, [roomId]);
  // The account remembers library rows too, so Recent is the same on the phone.
  const touchRemote = useRef(mine.onTouch);
  touchRemote.current = mine.onTouch;
  const touch = useCallback(
    (key: string) => {
      setRecent((ring) => {
        const next = touchRecent(ring, key);
        saveRecent(roomId, next);
        return next;
      });
      touchRemote.current(key);
    },
    [roomId],
  );
  /** Recent: this device's ring first, then what the account saw elsewhere. */
  const recentKeys = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const k of [...recent, ...mine.recent]) {
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(k);
    }
    return out;
  }, [recent, mine.recent]);
  const toggleLocalFavorite = useCallback(
    (key: string) => {
      setFavorites((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        saveFavorites(roomId, next);
        track("material_favorite", { detail: next.has(key) ? "on" : "off", value: 1 });
        return next;
      });
    },
    [roomId],
  );

  // The rows, per place.
  const mineById = useMemo(() => new Map(mine.clips.map((c) => [c.id, c])), [mine.clips]);
  const midiById = useMemo(() => new Map(mine.midi.map((m) => [m.id, m])), [mine.midi]);
  const projectItems = useMemo(() => itemsFromLibrary(library), [library]);
  // A seed's clip can be kept when its audio is in the shared pool; a MIDI clip always can.
  const seedItems = useMemo(
    () =>
      itemsFromReference(mine.seeds, objectUrl, tempo).map((it) => {
        if (!it.payload || !("reference" in it.payload)) return it;
        const audioKey = parseReference(it.payload.reference)?.audioKey ?? "";
        return referenceMidiOf(audioKey) === null && poolHashAnyChannel(audioKey) === null ? { ...it, payload: null } : it;
      }),
    [mine.seeds, tempo],
  );
  const mineItems = useMemo(
    () => [...mine.clips.map((c) => itemFromMyClip(c, tempo, objectUrl(c.audioKey))), ...mine.midi.map((m) => itemFromMyMidi(m, tempo))],
    [mine.clips, mine.midi, tempo],
  );
  const allByKey = useMemo(() => {
    const m = new Map<string, MaterialItem>();
    for (const it of projectItems) m.set(it.key, it);
    for (const it of mineItems) m.set(it.key, it);
    for (const it of seedItems) m.set(it.key, it);
    return m;
  }, [projectItems, mineItems, seedItems]);
  // Mine's stars live on the server; the room's are this device's.
  const starredMine = useMemo(() => {
    const out = new Set<string>();
    for (const c of mine.clips) if (c.starred) out.add(`mine:${c.id}`);
    for (const m of mine.midi) if (m.starred) out.add(`midi:${m.id}`);
    return out;
  }, [mine.clips, mine.midi]);
  const allFavorites = useMemo(() => new Set([...favorites, ...starredMine]), [favorites, starredMine]);
  const toggleFavorite = useCallback(
    (key: string) => {
      const item = allByKey.get(key);
      if (item?.place === "mine" && item.payload) {
        mine.onStar(item.payload, !starredMine.has(key));
        track("material_favorite", { detail: starredMine.has(key) ? "off" : "on", value: 1 });
      } else toggleLocalFavorite(key);
    },
    [allByKey, mine, starredMine, toggleLocalFavorite],
  );

  const filtering = query.trim() !== "" || chips.size > 0;
  // Mine, in the folder on screen. Live searches the selected sidebar entry
  // and everything under it; a plain look at a folder is just that folder.
  const subtree = useMemo(() => folderSubtree(mine.folders, folderId), [mine.folders, folderId]);
  const mineInView = useMemo(
    () => (smartId !== null || tagBrowse !== null ? mineItems : filtering ? mineItems.filter((it) => subtree.has(it.folderId)) : mineItems.filter((it) => it.folderId === folderId)),
    [mineItems, filtering, subtree, folderId, smartId, tagBrowse],
  );
  const placeItems = useMemo((): MaterialItem[] => {
    if (filter === "project") return projectItems;
    if (filter === "mine") return mineInView;
    if (filter === "seeds") return seedItems;
    return recentKeys.map((k) => allByKey.get(k)).filter((it): it is MaterialItem => it !== undefined);
  }, [filter, projectItems, mineInView, seedItems, recentKeys, allByKey]);
  const folderRows = useMemo(() => (filter === "mine" && !filtering && tagBrowse === null ? childFolders(mine.folders, folderId) : []), [filter, filtering, mine.folders, folderId, tagBrowse]);
  const smartRows = useMemo(() => (filter === "mine" && !filtering && folderId === null && tagBrowse === null ? mine.smartFolders : []), [filter, filtering, folderId, mine.smartFolders, tagBrowse]);
  /** Every stored tag in Mine, by group, with counts (kind and family words aside). */
  const tagIndex = useMemo((): TagIndexGroup[] => tagIndexOf(mineItems.map((it) => it.tags.filter((t) => !KIND_TAGS.has(t)))), [mineItems]);
  /** The same, for what is in reach — this folder and everything under it,
   * or a smart folder's rows — the filter view's counts. */
  const tagIndexHere = useMemo((): TagIndexGroup[] => {
    const reach = smartId !== null || tagBrowse !== null ? mineItems : mineItems.filter((it) => subtree.has(it.folderId));
    return tagIndexOf(reach.map((it) => it.tags.filter((t) => !KIND_TAGS.has(t))));
  }, [mineItems, subtree, smartId, tagBrowse]);
  /** Suggestions in reach, by tag, when the search lets them in — the filter view's "+n". */
  const suggestedCountsHere = useMemo((): Map<string, number> => {
    const out = new Map<string, number>();
    if (!chips.has(SUGGESTED_CHIP) && !chips.has(UNREVIEWED_CHIP)) return out;
    const reach = smartId !== null || tagBrowse !== null ? mineItems : mineItems.filter((it) => subtree.has(it.folderId));
    for (const it of reach) for (const t of it.suggested) out.set(t, (out.get(t) ?? 0) + 1);
    return out;
  }, [mineItems, subtree, smartId, tagBrowse, chips]);
  /** Colours in reach, with counts — the filter view's Colour row. */
  const colorCountsHere = useMemo((): Array<{ value: number; count: number }> => {
    const reach = smartId !== null || tagBrowse !== null ? mineItems : mineItems.filter((it) => subtree.has(it.folderId));
    const counts = new Map<number, number>();
    for (const it of reach) if (it.swatch !== null) counts.set(it.swatch, (counts.get(it.swatch) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => a[0] - b[0]).map(([value, count]) => ({ value, count }));
  }, [mineItems, subtree, smartId, tagBrowse]);
  /** Rows with a suggestion still waiting on the producer: the review queue's size. */
  const reviewCount = useMemo(() => mineItems.filter((it) => it.suggested.length > 0).length, [mineItems]);
  const tagGroupRows = useMemo(() => (filter === "mine" && tagBrowse !== null && tagBrowse.group === null && !filtering ? tagIndex : []), [filter, tagBrowse, filtering, tagIndex]);
  const tagValueRows = useMemo(() => (filter === "mine" && tagBrowse !== null && tagBrowse.group !== null && !filtering ? (tagIndex.find((g) => g.id === tagBrowse.group)?.values ?? []) : []), [filter, tagBrowse, filtering, tagIndex]);
  const tagGroupOpen = tagBrowse?.group ? (tagIndex.find((g) => g.id === tagBrowse.group) ?? null) : null;
  const chosenTags = useMemo(() => [...chips].filter((c) => c.startsWith("tag:")).map((c) => c.slice(4)), [chips]);
  const smartOpen = useMemo(() => (smartId ? (mine.smartFolders.find((s) => s.id === smartId) ?? null) : null), [smartId, mine.smartFolders]);
  /** How many rows each saved search holds right now. */
  const smartCounts = useMemo(() => {
    const out = new Map<string, number>();
    for (const sf of mine.smartFolders) {
      const f = { query: parseQuery(sf.query), chips: new Set(sf.chips as MaterialChip[]), favorites: allFavorites };
      out.set(sf.id, mineItems.filter((it) => matches(it, f)).length);
    }
    return out;
  }, [mine.smartFolders, mineItems, allFavorites]);
  /** Whether the open smart folder's search has been changed since it was saved. */
  const smartDirty = smartOpen !== null && (smartOpen.query !== query.trim() || smartOpen.chips.length !== chips.size || smartOpen.chips.some((c) => !chips.has(c as MaterialChip)));
  const counts = useMemo(() => folderCounts(mine.folders, mineItems), [mine.folders, mineItems]);
  /** Bytes under each folder, so a big one shows what it costs of the quota. */
  const bytesIn = useMemo(() => folderBytes(mine.folders, mineItems), [mine.folders, mineItems]);
  const path = useMemo(() => folderPath(mine.folders, folderId), [mine.folders, folderId]);
  const folderName = useMemo(() => new Map(mine.folders.map((f) => [f.id, f.name])), [mine.folders]);

  // The chips this place offers: kinds, ★, then one per track that has
  // material (Project) or the library's own tags (Mine).
  const chipRow = useMemo((): Array<{ chip: MaterialChip; label: string; color?: string; group?: string }> => {
    const row: Array<{ chip: MaterialChip; label: string; color?: string; group?: string }> = [
      { chip: "loop", label: "Loops" },
      { chip: "oneshot", label: "One-shots" },
    ];
    if (placeItems.some((i) => i.kind === "midi")) row.push({ chip: "midi", label: "MIDI" });
    row.push({ chip: "favorite", label: "★" });
    // Suggestions never filter unless asked: these two chips are the ask,
    // beside ★ so they are in view when on.
    const mineRows = filter === "mine" || filter === "recent";
    if (mineRows && reviewCount > 0) {
      row.push({ chip: SUGGESTED_CHIP, label: "Suggested too" });
      row.push({ chip: UNREVIEWED_CHIP, label: "Needs review" });
    }
    // What no seed has used yet — once something has been, so the chip means something.
    if (mineRows && mineItems.some((it) => it.uses > 0)) row.push({ chip: UNUSED_CHIP, label: "Unused" });
    const batchOn = [...chips].find((c) => c.startsWith("batch:"));
    if (batchOn) row.push({ chip: batchOn, label: "This drop" });
    for (const c of chips) if (c.startsWith("color:")) row.push({ chip: c, label: `Colour ${Number(c.slice(6)) + 1}`, color: SWATCHES[Number(c.slice(6))] ?? SWATCHES[0] });
    if (filter === "seeds") {
      // One chip per seed, newest touched first — the seeds are the folders here.
      const seen = new Set<string>();
      for (const it of placeItems) {
        if (it.seedId === null || seen.has(it.seedId)) continue;
        seen.add(it.seedId);
        if (seen.size > 8) break;
        row.push({ chip: `seed:${it.seedId}`, label: it.seedTitle ?? "Seed" });
      }
    } else if (filter === "project") {
      const seen = new Set<number>();
      for (const it of placeItems) {
        if (it.trackIndex === null || seen.has(it.trackIndex)) continue;
        seen.add(it.trackIndex);
        row.push({ chip: `track:${it.trackIndex}`, label: cleanTrackName(it.trackName ?? ""), color: clipColor(it.color ?? 0, it.trackIndex) });
      }
    } else {
      // Live's filter groups first (Drums · Sounds · Key …), by how many rows
      // carry them, then the free words — eight chips, the row scrolls.
      const counts = new Map<string, number>();
      for (const it of placeItems) for (const t of it.tags) if (!["loop", "oneshot", "midi", "session"].includes(t)) counts.set(t, (counts.get(t) ?? 0) + 1);
      const byCount = [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([t]) => t);
      const grouped = sortTags(byCount.filter((t) => parseTag(t).group !== null)).slice(0, 5);
      const free = byCount.filter((t) => parseTag(t).group === null).slice(0, Math.max(3, 8 - grouped.length));
      for (const t of [...grouped, ...free]) row.push({ chip: `tag:${t}`, label: tagLabel(t), group: parseTag(t).group ?? undefined });
    }
    return row;
  }, [placeItems, mineItems, filter, reviewCount, chips]);

  const parsed = useMemo(() => parseQuery(query), [query]);
  const shown = useMemo(() => {
    const kept = placeItems.filter((it) => matches(it, { query: parsed, chips, favorites: allFavorites }));
    // Recent keeps its own order (newest use first); the others sort.
    return filter === "recent" && (sort === null || sort === "recent") ? kept : sortItems(kept, sort ?? defaultSort(filter));
  }, [placeItems, parsed, chips, allFavorites, sort, filter]);
  shownRef.current = shown;
  // Long lists render in pages: the first ROW_PAGE rows now, the next as the
  // end comes into view — hundreds of rows never go into the DOM at once.
  const [rowLimit, setRowLimit] = useState(ROW_PAGE);
  useEffect(() => {
    setRowLimit(ROW_PAGE);
  }, [filter, parsed, chips, sort, folderId, smartId, tagBrowse]);
  const moreRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    const el = moreRef.current;
    if (!el || shown.length <= rowLimit || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setRowLimit((n) => n + ROW_PAGE);
    });
    io.observe(el);
    return () => io.disconnect();
  }, [shown.length, rowLimit]);

  const clearAll = (): void => {
    setQuery("");
    setChips(new Set());
    setSmartId(null);
    setTagBrowse(null);
  };
  const toggleChip = (chip: MaterialChip): void => {
    setChips((prev) => {
      const next = new Set(prev);
      if (next.has(chip)) next.delete(chip);
      else next.add(chip);
      return next;
    });
    track("material_filter", { detail: chip.split(":")[0] ?? chip, value: 1 });
  };

  // `#` completion for the word being typed.
  const lastWord = query.split(/\s+/).pop() ?? "";
  const completions = useMemo(
    () => (lastWord.startsWith("#") ? completeTag(placeItems, lastWord.slice(1)).filter((t) => `#${t}` !== lastWord) : []),
    [lastWord, placeItems],
  );
  const complete = (tag: string): void => {
    const words = query.split(/\s+/);
    words[words.length - 1] = `#${tag}`;
    setQuery(`${words.join(" ")} `);
  };
  // "Did you mean": a `#word` no row answers, and the vocabulary tag it was
  // most likely after. The evaluator never guesses; this is one tap.
  const didYouMean = useMemo((): { word: string; tag: string } | null => {
    if (filter !== "mine" || shown.length > 0 || parsed.tags.length === 0) return null;
    for (const t of parsed.tags) {
      if (mineItems.some((it) => [...it.tags, ...it.suggested].some((tag) => tagAnswersTo(tag, t)))) continue;
      const tag = suggestTagFor(mineItems, t);
      if (tag && tag !== t) return { word: t, tag };
    }
    return null;
  }, [filter, shown.length, parsed.tags, mineItems]);
  const takeDidYouMean = (): void => {
    if (!didYouMean) return;
    setQuery(query.replace(new RegExp(`#${didYouMean.word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?=\\s|$)`, "i"), `#${didYouMean.tag}`));
    track("material_search", { detail: "did-you-mean", value: 1 });
  };

  // Preview: one at a time, in time with the Set unless Raw.
  const preview = useCallback(
    (item: MaterialItem) => {
      const src = item.playable;
      if (!src) return;
      if (player.auditioningId === src.id) {
        lastAudition.current = null;
        player.stopAudition();
        return;
      }
      // Level: every preview at one loudness, unless Raw asked for the file as it is.
      const options = { sync: !raw, loop: !raw && item.kind === "loop" && !playOn, level: !raw };
      if ("notes" in src) player.auditionMidiClip(src as MidiSource, options);
      else player.auditionClip(src as ClipSource, options);
      lastAudition.current = { key: item.key, startedAt: performance.now(), seconds: item.seconds };
      touch(item.key);
      track("material_preview", { detail: `${item.place}|${raw ? "raw" : "synced"}`, value: 1 });
    },
    [player, raw, playOn, touch],
  );
  useEffect(() => {
    if (player.auditioningId !== null || !playOn) return;
    const last = lastAudition.current;
    if (!last) return;
    // Ended by itself (it ran its length), not by a Stop: a stop before the
    // end leaves the elapsed time short. Sync waits for a bar first, which
    // only makes it longer.
    const elapsed = (performance.now() - last.startedAt) / 1000;
    if (elapsed < Math.max(0.2, last.seconds * 0.9)) return;
    const t = window.setTimeout(() => {
      if (auditioningRef.current !== null) return;
      const rows = shownRef.current;
      const at = rows.findIndex((i) => i.key === last.key);
      const next = rows.slice(at + 1).find((i) => i.playable !== null);
      lastAudition.current = null;
      if (!next) return;
      setActiveKey(next.key);
      preview(next);
    }, 120);
    return () => window.clearTimeout(t);
  }, [player.auditioningId, playOn, preview]);

  // ＋: the page knows where it lands; the row only knows which.
  const place = useCallback(
    (item: MaterialItem, via: "tap" | "enter") => {
      if (!item.payload) return;
      if ("material" in item.payload) onPlace?.(item.payload.material);
      else if ("reference" in item.payload) {
        const ref = parseReference(item.payload.reference);
        if (ref) mine.onPlaceReference(ref.seedId, ref.audioKey);
      } else if ("mineMidi" in item.payload) {
        const midi = midiById.get(item.payload.mineMidi);
        if (midi) mine.onPlaceMidi(midi);
      } else {
        const clip = mineById.get(item.payload.mine);
        if (clip) mine.onPlace(clip);
      }
      touch(item.key);
      track("material_place", { detail: `${item.place}|${via}`, value: 1 });
    },
    [onPlace, mine, mineById, midiById, touch],
  );

  // Folders: open one, make one, name one, move rows into one.
  const openFolder = useCallback((id: string | null) => {
    setFolderId(id);
    setActiveKey(null);
    setNaming(false);
    setRenaming(null);
    setSmartId(null);
    setTagBrowse(null);
    setQuery("");
    setChips(new Set());
    track("library_folder_opened", { value: 1 });
  }, []);
  /** Browse by tag: the groups, one group's values, or one value's rows. */
  const browseTags = useCallback((group: string | null, tag: string | null = null) => {
    setFolderId(null);
    setSmartId(null);
    setTagBrowse({ group });
    setQuery("");
    // The suggestion chips ride along: browsing by tag with "Suggested too"
    // on keeps showing the suggested rows.
    setChips((prev) => new Set<MaterialChip>([...[...prev].filter((c) => c === SUGGESTED_CHIP || c === UNREVIEWED_CHIP), ...(tag ? [`tag:${tag}` as MaterialChip] : [])]));
    setActiveKey(null);
    track("library_tag_browsed", { detail: tag ? "value" : group ? "group" : "groups", value: 1 });
  }, []);
  /** The review queue: the whole of Mine, rows with a suggestion waiting. */
  const openReview = useCallback(() => {
    setFolderId(null);
    setSmartId(null);
    setTagBrowse(null);
    setQuery("");
    setChips(new Set<MaterialChip>([UNREVIEWED_CHIP]));
    setActiveKey(null);
    track("library_review_opened", { value: 1 });
  }, []);
  /** A saved search, opened: its words and chips become the filter. */
  const openSmart = useCallback((sf: MySmartFolder) => {
    setFolderId(null);
    setTagBrowse(null);
    setSmartId(sf.id);
    setQuery(sf.query);
    setChips(new Set(sf.chips as MaterialChip[]));
    if (sf.sort && sf.sort in SORT_LABELS) setSort(sf.sort as MaterialSort);
    setActiveKey(null);
    track("library_smart_opened", { value: 1 });
  }, []);
  useImperativeHandle(
    handleRef,
    () => ({
      openFolder,
      openSmart,
      browseTags,
      openReview,
      addPicked: (list) => mine.onAddPicked(list, folderId),
      setSearch: (q, c) => {
        setQuery(q);
        setChips(new Set(c as MaterialChip[]));
      },
      saveSearch: () => setSavingSmart(true),
    }),
    [openFolder, openSmart, browseTags, openReview, mine, folderId],
  );
  const reviewOpen = chips.has(UNREVIEWED_CHIP);
  const chipList = useMemo(() => [...chips].sort(), [chips]);
  const queryTrim = query.trim();
  useEffect(() => {
    onWhere?.({ folderId, smartId, tagGroup: tagBrowse === null ? undefined : tagBrowse.group, review: reviewOpen, query: queryTrim, chips: chipList });
  }, [onWhere, folderId, smartId, tagBrowse, reviewOpen, queryTrim, chipList]);
  const saveSmart = async (name: string): Promise<void> => {
    setSavingSmart(false);
    const made = await mine.onCreateSmartFolder({ name, query: query.trim(), chips: [...chips], sort });
    if (made) {
      setSmartId(made.id);
      track("library_smart_created", { value: 1 });
    }
  };
  // Selection: a plain click picks one row; ⌘-click adds or drops one;
  // ⇧-click takes the range from the active row. A new place, folder or
  // filter empties it — the rows under the pointer are not the same rows.
  useEffect(() => {
    setSelected(new Set());
    setConfirmingDelete(false);
  }, [filter, folderId, parsed, chips]);
  useEffect(() => {
    if (filter !== "mine") setSelecting(false);
  }, [filter]);
  useEffect(() => {
    setActiveKey(null);
    setChips((prev) => {
      const next = new Set([...prev].filter((c) => chipBelongs(c, filter)));
      return next.size === prev.size ? prev : next;
    });
  }, [filter]);
  /** The rows a selection can hold: what is shown, of Mine, with something to act on. */
  const selectable = useMemo(() => shown.filter((i) => i.place === "mine" && i.payload !== null), [shown]);
  /** ⌘A and Select all: every row shown — Live's content pane, never the whole library. */
  const selectAll = useCallback(() => {
    setSelected(new Set(selectable.map((i) => i.key)));
    setActiveKey(selectable[0]?.key ?? null);
    track("library_select_all", { value: selectable.length });
  }, [selectable]);
  const clearSelection = useCallback(() => {
    setSelected(new Set());
    setConfirmingDelete(false);
  }, []);
  const selectRow = (key: string, mod: { shift: boolean; toggle: boolean; pure?: boolean }): void => {
    if (mod.pure) {
      // A checkbox: this row and nothing else changes.
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
      setActiveKey(key);
      return;
    }
    if (mod.shift && activeKey !== null) {
      const keys = shown.map((i) => i.key);
      const a = keys.indexOf(activeKey);
      const b = keys.indexOf(key);
      if (a >= 0 && b >= 0) {
        const range = keys.slice(Math.min(a, b), Math.max(a, b) + 1);
        setSelected((prev) => new Set([...prev, ...range]));
        return;
      }
    }
    if (mod.toggle) {
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        if (prev.size === 0 && activeKey !== null && activeKey !== key) next.add(activeKey);
        return next;
      });
      setActiveKey(key);
      return;
    }
    setSelected(new Set());
    setActiveKey(key);
  };
  /** The Mine rows the footer acts on: the selection, else the active row. */
  const tagTargets = useMemo((): MaterialItem[] => {
    if (filter !== "mine") return [];
    const picked = selected.size > 0 ? shown.filter((i) => selected.has(i.key)) : activeKey ? shown.filter((i) => i.key === activeKey) : [];
    return picked.filter((i) => i.place === "mine" && i.payload !== null);
  }, [filter, selected, activeKey, shown]);
  const tagDelta = (delta: TagDelta): void => {
    const payloads = tagTargets.map((i) => i.payload!);
    if (payloads.length === 0) return;
    if (payloads.length === 1) mine.onTag(payloads[0]!, delta);
    else mine.onTagMany(payloads, delta);
  };
  const acceptTempo = (item: MaterialItem, bpm: number): void => {
    if (item.payload && "mine" in item.payload) mine.onUpdateClip(item.payload.mine, { bpm, kind: "loop", tags: { unset: [`tempo:${bpm}`] } });
  };

  const createFolder = async (name: string): Promise<void> => {
    const made = await mine.onCreateFolder(name, folderId);
    setNaming(false);
    if (made) track("library_folder_created", { value: 1 });
  };
  const moveTo = (items: readonly MaterialPayload[], to: string | null): void => {
    mine.onMove(items, to);
    setMoving(null);
  };
  const moveFolderTo = (id: string, to: string | null): void => {
    if (canMoveFolder(mine.folders, id, to, LIBRARY_FOLDER_MAX_DEPTH)) mine.onMoveFolder(id, to);
    setMovingFolder(null);
  };
  /** A library row (or a folder row) dragged (mouse) over a folder row. */
  const folderDrop = (to: string | null) => ({
    onDragOver: (e: DragEvent) => {
      const types = e.dataTransfer.types;
      if (types.includes(MINE_FOLDER_DRAG_TYPE)) {
        // A folder takes a folder only where the picker would let it go.
        if (dragFolder.current === null || !canMoveFolder(mine.folders, dragFolder.current, to, LIBRARY_FOLDER_MAX_DEPTH)) return;
      } else if (!types.includes(MINE_DRAG_TYPE) && !types.includes(MINE_MIDI_DRAG_TYPE)) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "move";
      if (dropFolder !== to) setDropFolder(to);
    },
    onDragLeave: () => setDropFolder((cur) => (cur === to ? null : cur)),
    onDrop: (e: DragEvent) => {
      const clip = e.dataTransfer.getData(MINE_DRAG_TYPE);
      const midi = e.dataTransfer.getData(MINE_MIDI_DRAG_TYPE);
      const folder = e.dataTransfer.getData(MINE_FOLDER_DRAG_TYPE);
      if (!clip && !midi && !folder) return;
      e.preventDefault();
      e.stopPropagation();
      setDropFolder(null);
      dragFolder.current = null;
      if (folder) moveFolderTo(folder, to);
      else moveTo(clip ? [{ mine: clip }] : [{ mineMidi: midi }], to);
    },
  });

  // Files from the desktop, anywhere on the shelf, go to Mine. The page
  // leaves them alone: a drop there opens its upload flow instead.
  const dropProps = page ? {} : {
    onDragOver: (e: DragEvent) => {
      if (!e.dataTransfer.types.includes("Files")) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
      setFileOver(true);
    },
    onDragLeave: () => setFileOver(false),
    onDrop: (e: DragEvent) => {
      if (!e.dataTransfer.types.includes("Files")) return;
      e.preventDefault();
      setFileOver(false);
      onFilter("mine");
      mine.onAddFiles(e.dataTransfer, filter === "mine" ? folderId : null);
    },
  };

  // Keys inside the list: ↑↓ walk the rows, ⏎ places, ⇧⏎ previews, Esc clears.
  const listRef = useRef<HTMLUListElement>(null);
  // A new filter, place or sort starts the list at the top: the rows under
  // the finger are not the rows that were there, so a kept scroll position
  // lands on something unrelated.
  const scrollerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: 0 });
  }, [filter, parsed, chips, sort, folderId]);
  // The page's columns (length · tempo · added) need room beside the name:
  // measured from the list itself, since beside the inspector a "desktop"
  // window leaves the list no wider than a phone.
  const [listWide, setListWide] = useState(false);
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const check = (): void => setListWide(el.clientWidth >= COLUMNS_MIN_PX);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [page]);
  const onListKey = (e: ReactKeyboardEvent): void => {
    if (shown.length === 0) return;
    const at = shown.findIndex((i) => i.key === activeKey);
    const go = (i: number): void => {
      const next = shown[Math.max(0, Math.min(shown.length - 1, i))];
      if (!next) return;
      setActiveKey(next.key);
      listRef.current?.querySelector<HTMLElement>(`[data-key="${CSS.escape(next.key)}"]`)?.scrollIntoView({ block: "nearest" });
    };
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "a" && filter === "mine") {
      e.preventDefault();
      selectAll();
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const to = Math.max(0, Math.min(shown.length - 1, e.key === "ArrowDown" ? at + 1 : at - 1));
      // ⇧: the selection grows to take the row stepped onto (and the one left).
      if (e.shiftKey && filter === "mine") {
        const from = shown[at]?.key;
        const next = shown[to]?.key;
        setSelected((prev) => new Set([...prev, ...(from ? [from] : []), ...(next ? [next] : [])]));
      }
      go(to);
    } else if (e.key === "Enter" && at >= 0) {
      e.preventDefault();
      const item = shown[at]!;
      if (e.shiftKey || page) preview(item);
      else place(item, "enter");
    } else if (e.key === "Escape" && selected.size > 0) {
      e.preventDefault();
      clearSelection();
    } else if (e.key === "Escape" && filtering) {
      e.preventDefault();
      clearAll();
    } else if (e.key === "Backspace" && filter === "mine" && !filtering && folderId !== null) {
      // Live's ←: out of the folder.
      e.preventDefault();
      openFolder(path[path.length - 2]?.id ?? null);
    }
  };

  // Two readings, two shapes. Filtering: "5 of 14" (rows shown of rows in
  // this place). Not filtering: what the place holds — for Project both
  // numbers, since 14 distinct sounds placed 26 times is the point of the
  // grouping. "Filtering" is the controls, not a coincidence of counts: a
  // chip that happens to keep every row still reads as a filter.
  const total = placeItems.length;
  const countLabel = filtering
    ? `${shown.length} of ${total}`
    : filter === "project" && library.totalPlacements !== total
      ? `${total} clip${total === 1 ? "" : "s"} · ${library.totalPlacements} placed`
      : filter === "mine" && tagGroupRows.length > 0
        ? `${tagGroupRows.reduce((n, g) => n + g.values.length, 0)} tags`
        : filter === "mine" && tagValueRows.length > 0
          ? `${tagValueRows.length} ${tagValueRows.length === 1 ? "value" : "values"}`
          : filter === "mine" && folderRows.length > 0
            ? `${folderRows.length} folder${folderRows.length === 1 ? "" : "s"} · ${total} clip${total === 1 ? "" : "s"}`
            : `${total} clip${total === 1 ? "" : "s"}`;

  return (
    // `min-w-0` is load-bearing: a flex child defaults to min-width:auto and
    // would size to its widest row instead of to the rail, pushing the ＋ off
    // the screen edge.
    <div className={cn("relative flex min-h-0 w-full min-w-0 flex-col", fileOver && "rounded-xl outline outline-2 outline-primary/60")} {...dropProps}>
      {/* Header: sticky at every size — the place, the search, the chips. */}
      <div className={cn("shrink-0 pb-2", page ? "px-0" : "px-3")}>
        {!page && <PlaceSwitch value={filter} onChange={onFilter} recentCount={recentKeys.length} seedCount={seedItems.length} />}
        {filter === "mine" && mine.signedIn && (
          <PathLine
            path={path}
            smart={smartOpen}
            tagTrail={tagBrowse ? { group: tagGroupOpen, tag: tagBrowse.group && chosenTags.length === 1 ? chosenTags[0]! : null, onGroups: () => browseTags(null), onGroup: () => tagGroupOpen && browseTags(tagGroupOpen.id) } : null}
            onOpen={openFolder}
            onNew={() => setNaming(true)}
            canNew={path.length < LIBRARY_FOLDER_MAX_DEPTH && smartOpen === null}
            dropTarget={folderDrop}
            dropFolder={dropFolder}
          />
        )}
        <div className="mt-2 flex items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-2 top-1/2 size-3 -translate-y-1/2 text-muted-foreground" />
            <input
              id={MATERIAL_SEARCH_ID}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  clearAll();
                  (e.target as HTMLInputElement).blur();
                } else if (e.key === "ArrowDown") {
                  // Live: ↓ from the search field jumps into the results.
                  e.preventDefault();
                  setActiveKey(shown[0]?.key ?? null);
                  listRef.current?.focus();
                } else if (e.key === "Tab" && completions[0]) {
                  e.preventDefault();
                  complete(completions[0]);
                } else if (e.key === "Enter") {
                  track("material_search", { detail: parsed.tags.length > 0 ? "tag" : "text", value: 1 });
                }
              }}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              placeholder={filter === "mine" ? "Find in your clips · #tag" : "Find material · #tag"}
              aria-label="Find material"
              autoComplete="off"
              className="h-9 w-full min-w-0 rounded-lg border border-panelline bg-control pl-7 pr-7 text-xs outline-none placeholder:text-muted-foreground/70 focus:border-controlline wide:h-7"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Clear search text"
                className="absolute right-1 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:text-foreground wide:size-5"
              >
                <X className="size-3" />
              </button>
            )}
            {searchFocused && completions.length > 0 && (
              <ul role="listbox" aria-label="Tags" className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-lg border border-panelline bg-raised shadow-lg">
                {completions.map((t) => (
                  <li key={t}>
                    <button type="button" role="option" aria-selected={false} onMouseDown={(e) => e.preventDefault()} onClick={() => complete(t)} className="flex h-9 w-full items-center gap-2 px-3 text-left text-xs hover:bg-white/10 wide:h-7">
                      <span className="truncate">{parseTag(t).group ? tagLabel(t) : `#${t}`}</span>
                      {parseTag(t).group && <span className="ml-auto shrink-0 font-mono text-[10px] text-muted-foreground">#{t}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <SortMenu value={sort ?? defaultSort(filter)} onChange={setSort} />
          {filter === "mine" && mine.signedIn && tagIndex.length > 0 && (
            <button
              type="button"
              role="switch"
              aria-checked={filterView}
              aria-label="Filter view"
              onClick={() => setFilterView((v) => !v)}
              title={filterView ? "Hide the filter view" : "Show every tag, by group, with counts"}
              className={cn(
                "flex size-9 shrink-0 items-center justify-center rounded-lg border wide:size-7",
                filterView ? "border-primary bg-primary/15 text-foreground" : "border-panelline text-muted-foreground hover:text-foreground",
              )}
            >
              <ListFilter className="size-4 wide:size-3.5" />
            </button>
          )}
          {!previewOutside && (
          <>
          <button
            type="button"
            role="switch"
            aria-checked={playOn}
            onClick={() => setPlayOn(!playOn)}
            title={playOn ? "Play on: when a preview ends, the next row plays (a loop plays once)" : "Play each row once, then stop"}
            aria-label="Play on"
            className={cn(
              "hidden size-9 shrink-0 items-center justify-center rounded-lg border sm:flex wide:size-7",
              playOn ? "border-primary bg-primary/15 text-foreground" : "border-panelline text-muted-foreground hover:text-foreground",
            )}
          >
            <SkipForward className="size-3.5 wide:size-3" />
          </button>
          <button
            type="button"
            role="switch"
            aria-checked={raw}
            onClick={() => setRaw((v) => !v)}
            title={raw ? "Raw: previews play dry, from the top, once" : "Previews start on the next bar and loop, in time with the Set"}
            className={cn(
              "h-9 shrink-0 rounded-lg border px-2 text-[11px] font-semibold wide:h-7",
              raw ? "border-primary bg-primary/15 text-foreground" : "border-panelline text-muted-foreground hover:text-foreground",
            )}
          >
            Raw
          </button>
          </>
          )}
        </div>
        {/* Chips scroll; the count and Clear stay put at the right, so a
            thumb can always undo a filter without scrolling the row. */}
        <div className="mt-2 flex items-center gap-2">
          <div className="scrollbar-none flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto pb-0.5 [mask-image:linear-gradient(to_right,black_calc(100%-20px),transparent)]">
            {chipRow.map(({ chip, label, color, group }) => {
            const on = chips.has(chip);
            return (
              <button
                key={chip}
                type="button"
                role="checkbox"
                aria-checked={on}
                onClick={() => toggleChip(chip)}
                title={group ? `${group}: ${label}` : undefined}
                className={cn(
                  "flex h-8 shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-[11px] font-semibold wide:h-6",
                  on ? "border-foreground/60 bg-white/15 text-foreground" : "border-panelline text-muted-foreground hover:text-foreground",
                )}
              >
                {color && <span className="size-2 rounded-full" style={{ backgroundColor: color }} />}
                {chip === "favorite" ? <Star className={cn("size-3", on && "fill-current")} /> : label}
              </button>
            );
          })}
          </div>
          {filter === "mine" && mine.signedIn && placeItems.length > 0 && (
            <button
              type="button"
              role="switch"
              aria-checked={selecting}
              aria-label="Select rows"
              onClick={() => {
                setSelecting((v) => !v);
                if (selecting) clearSelection();
              }}
              title={selecting ? "Done selecting" : "Select several rows (a mouse can ⇧-click and ⌘-click instead)"}
              className={cn(
                "flex size-8 shrink-0 items-center justify-center rounded-full border wide:size-6",
                selecting ? "border-primary bg-primary/15 text-foreground" : "border-panelline text-muted-foreground hover:text-foreground",
              )}
            >
              <ListChecks className="size-3.5 wide:size-3" />
            </button>
          )}
          <span className="shrink-0 whitespace-nowrap text-[11px] text-muted-foreground" aria-live="polite">
            {countLabel}
          </span>
          {filtering && filter === "mine" && mine.signedIn && smartOpen === null && (
            <button
              type="button"
              onClick={() => setSavingSmart(true)}
              title="Keep this search as a smart folder"
              aria-label="Save as smart folder"
              className="flex size-8 shrink-0 items-center justify-center rounded-full border border-panelline text-muted-foreground hover:text-foreground wide:size-6"
            >
              <BookmarkPlus className="size-3.5" />
            </button>
          )}
          {smartOpen !== null && smartDirty && (
            <button
              type="button"
              onClick={() => mine.onUpdateSmartFolder(smartOpen.id, { query: query.trim(), chips: [...chips], sort })}
              title="Save the changed search to this smart folder"
              className="flex h-8 shrink-0 items-center gap-1 rounded-full border border-primary/50 px-2 text-[11px] font-semibold text-primary hover:bg-primary/10 wide:h-6"
            >
              <Check className="size-3" />
              Update
            </button>
          )}
          {(filtering || smartOpen !== null) && (
            <button
              type="button"
              onClick={clearAll}
              title={smartOpen ? "Leave the smart folder and clear its search" : "Clear the search and every chip"}
              className="flex h-8 shrink-0 items-center gap-1 rounded-full border border-primary/50 px-2 text-[11px] font-semibold text-primary hover:bg-primary/10 wide:h-6"
            >
              <X className="size-3" />
              {smartOpen ? "Close" : "Clear"}
            </button>
          )}
        </div>
        {filter === "mine" && filterView && (tagIndexHere.length > 0 || colorCountsHere.length > 0) && (
          <div className="mt-2 flex flex-col gap-1.5 rounded-lg border border-panelline bg-panel/60 p-2" aria-label="Filter view">
            {colorCountsHere.length > 0 && (
              <div className="flex items-start gap-2">
                <span className="w-16 shrink-0 truncate pt-1.5 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Colour</span>
                <div className="flex min-w-0 flex-1 flex-wrap gap-1">
                  {colorCountsHere.map(({ value, count }) => {
                    const on = chips.has(`color:${value}`);
                    return (
                      <button
                        key={value}
                        type="button"
                        role="checkbox"
                        aria-checked={on}
                        aria-label={`Colour ${value + 1}`}
                        onClick={() => toggleChip(`color:${value}`)}
                        className={cn(
                          "flex h-7 items-center gap-1 rounded-full border px-2 text-[11px] wide:h-6",
                          on ? "border-foreground/60 bg-white/15 text-foreground" : "border-panelline text-muted-foreground hover:text-foreground",
                        )}
                      >
                        <span className="size-2.5 rounded-full" style={{ backgroundColor: SWATCHES[value] ?? SWATCHES[0] }} />
                        <span className="font-mono text-[9px] opacity-60">{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {tagIndexHere.map((g) => (
              <div key={g.id} className="flex items-start gap-2">
                <button type="button" onClick={() => browseTags(g.id)} className="w-16 shrink-0 truncate pt-1.5 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground" title={`Browse ${g.label}`}>
                  {g.label}
                </button>
                <div className="flex min-w-0 flex-1 flex-wrap gap-1">
                  {g.values.map((v) => {
                    const on = chips.has(`tag:${v.tag}`);
                    return (
                      <button
                        key={v.tag}
                        type="button"
                        role="checkbox"
                        aria-checked={on}
                        onClick={() => toggleChip(`tag:${v.tag}`)}
                        className={cn(
                          "flex h-7 items-center gap-1 rounded-full border px-2 text-[11px] wide:h-6",
                          on ? "border-foreground/60 bg-white/15 text-foreground" : "border-panelline text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {g.id === "words" && <Hash className="size-2.5" />}
                        {v.label}
                        <span className="font-mono text-[9px] opacity-60">{v.count}</span>
                        {(suggestedCountsHere.get(v.tag) ?? 0) > 0 && (
                          <span className="font-mono text-[9px] text-primary/80" title="Suggested, not yet kept">
                            +{suggestedCountsHere.get(v.tag)}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        {filter === "mine" && mine.signedIn && !inspect && (selected.size > 0 || selecting) && (
          <SelectionBar
            count={tagTargets.length}
            total={selectable.length}
            selecting={selecting}
            starredAll={tagTargets.length > 0 && tagTargets.every((i) => allFavorites.has(i.key))}
            confirmingDelete={confirmingDelete}
            onSelectAll={selectAll}
            onClear={clearSelection}
            onDone={() => {
              setSelecting(false);
              clearSelection();
            }}
            onMove={() => setMoving(tagTargets.map((i) => i.payload!))}
            onStar={(starred) => {
              for (const it of tagTargets) if (it.payload) mine.onStar(it.payload, starred);
            }}
            onDelete={() => setConfirmingDelete(true)}
            onDeleteConfirm={() => {
              for (const it of tagTargets) {
                if (!it.payload) continue;
                if ("mine" in it.payload) mine.onDelete(it.payload.mine);
                else if ("mineMidi" in it.payload) mine.onDeleteMidi(it.payload.mineMidi);
              }
              clearSelection();
            }}
            onDeleteCancel={() => setConfirmingDelete(false)}
          />
        )}
        {filter === "mine" && mine.adding && (
          <p className={cn("mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px]", mine.adding.error ? "text-removed" : "text-muted-foreground")} role="status">
            {mine.adding.error ? null : mine.adding.done ? <Check className="size-3 text-primary" /> : <Loader2 className="size-3 animate-spin" />}
            {mine.adding.text}
            {mine.adding.onRetry && (
              <button type="button" onClick={mine.adding.onRetry} className="font-semibold text-primary underline">
                Retry
              </button>
            )}
            {mine.adding.batch && !chips.has(`batch:${mine.adding.batch.id}`) && (
              <button
                type="button"
                onClick={() => {
                  setFolderId(null);
                  setSmartId(null);
                  setTagBrowse(null);
                  setQuery("");
                  setChips(new Set<MaterialChip>([`batch:${mine.adding!.batch!.id}`]));
                  track("library_batch_reviewed", { value: mine.adding!.batch!.count });
                }}
                className="font-semibold text-primary underline"
              >
                Review {mine.adding.batch.count === 1 ? "it" : `these ${mine.adding.batch.count}`}
              </button>
            )}
            {mine.adding.batch && (
              <UndoBatch count={mine.adding.batch.count} onUndo={() => mine.onUndoBatch(mine.adding!.batch!.id)} />
            )}
          </p>
        )}
      </div>

      {filter === "mine" && mine.adding?.progress && <UploadProgress view={mine.adding.progress} />}

      {/* The rows. */}
      <div ref={scrollerRef} className={cn("scrollbar-thin min-h-0 flex-1 overflow-auto pb-3", page ? "px-0" : "px-3")}>
        {filter === "mine" && !mine.signedIn ? (
          <div className="flex flex-col items-center gap-2 px-4 py-6 text-center">
            <p className="text-sm text-muted-foreground">Your own loops and one-shots, ready for any project.</p>
            <button type="button" onClick={mine.onSignIn} className="h-9 rounded-lg bg-primary px-4 text-xs font-bold text-primary-foreground">
              Sign in to see your clips
            </button>
          </div>
        ) : filter === "mine" && mine.loading ? (
          <p className="flex justify-center py-6">
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          </p>
        ) : filter === "mine" && mine.error && mineItems.length === 0 && mine.folders.length === 0 ? (
          <div role="alert" className="flex flex-col items-center gap-2 px-4 py-6 text-center">
            <p className="text-sm font-semibold">Your library couldn't be loaded.</p>
            <p className="text-xs text-muted-foreground">{mine.error}</p>
            <p className="text-xs text-muted-foreground">Nothing is lost: this is the request failing, not the clips.</p>
            {mine.onRetry && (
              <button type="button" onClick={mine.onRetry} className="h-9 rounded-lg border border-panelline px-4 text-xs font-semibold hover:bg-white/5">
                Try again
              </button>
            )}
          </div>
        ) : tagGroupRows.length > 0 || tagValueRows.length > 0 ? (
          <ul role="list" aria-label={tagGroupOpen ? `${tagGroupOpen.label} tags` : "Tag groups"} className="flex flex-col gap-1.5">
            {tagGroupRows.map((g) => (
              <TagBrowseRow key={g.id} icon={g.id === "words" ? "hash" : "tags"} label={g.label} sub={`${g.values.length} tag${g.values.length === 1 ? "" : "s"} · ${g.count} clip${g.count === 1 ? "" : "s"}`} onOpen={() => browseTags(g.id)} />
            ))}
            {tagValueRows.map((v) =>
              renamingTag === v.tag ? (
                <li key={v.tag}>
                  <FolderNameRow
                    initial={v.tag}
                    placeholder="Tag name — or a tag to merge into, like drums:kick"
                    kind="tag"
                    onDone={(name) => {
                      const to = normalizeTag(name);
                      setRenamingTag(null);
                      if (to !== "" && to !== v.tag && validTag(to)) mine.onRenameTag(v.tag, to);
                    }}
                    onCancel={() => setRenamingTag(null)}
                  />
                </li>
              ) : (
                <TagBrowseRow
                  key={v.tag}
                  icon={tagGroupOpen?.id === "words" ? "hash" : "tag"}
                  label={v.label}
                  sub={`${v.count} clip${v.count === 1 ? "" : "s"}`}
                  onOpen={() => browseTags(tagGroupOpen!.id, v.tag)}
                  onRename={tagGroupOpen?.id === "words" ? () => setRenamingTag(v.tag) : undefined}
                />
              ),
            )}
          </ul>
        ) : placeItems.length === 0 && folderRows.length === 0 && smartRows.length === 0 && !naming && !savingSmart ? (
          <EmptyPlace place={filter} inFolder={folderId !== null} page={page} />
        ) : shown.length === 0 && folderRows.length === 0 && smartRows.length === 0 && !naming && !savingSmart ? (
          <p className="py-6 text-center text-xs text-muted-foreground">
            Nothing matches.{" "}
            {didYouMean && (
              <>
                Did you mean{" "}
                <button type="button" onClick={takeDidYouMean} className="font-semibold text-primary underline" title={`Search for ${tagLabel(didYouMean.tag)} instead`}>
                  #{didYouMean.tag}
                </button>
                ?{" "}
              </>
            )}
            <button type="button" onClick={clearAll} className="underline">Clear</button>
          </p>
        ) : (
          <ul
            ref={listRef}
            tabIndex={0}
            role="listbox"
            aria-label="Material"
            aria-activedescendant={activeKey ? `material-row-${activeKey}` : undefined}
            onKeyDown={onListKey}
            className="flex flex-col gap-1.5 outline-none"
          >
            {naming && (
              <li>
                <FolderNameRow placeholder="New folder" onDone={(name) => void createFolder(name)} onCancel={() => setNaming(false)} />
              </li>
            )}
            {savingSmart && (
              <li>
                <FolderNameRow placeholder="Name this search" smart onDone={(name) => void saveSmart(name)} onCancel={() => setSavingSmart(false)} />
              </li>
            )}
            {folderRows.map((f) =>
              renaming === f.id ? (
                <li key={f.id}>
                  <FolderNameRow
                    initial={f.name}
                    placeholder="Folder name"
                    onDone={(name) => {
                      mine.onRenameFolder(f.id, name);
                      setRenaming(null);
                    }}
                    onCancel={() => setRenaming(null)}
                  />
                </li>
              ) : (
                <FolderRow
                  key={f.id}
                  folder={f}
                  count={counts.get(f.id) ?? 0}
                  bytes={bytesIn.get(f.id) ?? 0}
                  quiet
                  over={dropFolder === f.id}
                  dropProps={folderDrop(f.id)}
                  onOpen={() => openFolder(f.id)}
                  onRename={() => setRenaming(f.id)}
                  onMove={() => setMovingFolder(f)}
                  onDragFolder={(dragging) => {
                    dragFolder.current = dragging ? f.id : null;
                  }}
                  onDelete={() => mine.onDeleteFolder(f.id)}
                  suggestTags={mine.folderTags(f.id)}
                  onSuggest={() => {
                    const inside = folderSubtree(mine.folders, f.id);
                    mine.onSuggest(mineItems.filter((it) => inside.has(it.folderId) && it.payload !== null).map((it) => it.payload!), mine.folderTags(f.id));
                  }}
                />
              ),
            )}
            {smartRows.map((sf) =>
              renamingSmart === sf.id ? (
                <li key={sf.id}>
                  <FolderNameRow
                    initial={sf.name}
                    placeholder="Smart folder name"
                    smart
                    onDone={(name) => {
                      mine.onUpdateSmartFolder(sf.id, { name });
                      setRenamingSmart(null);
                    }}
                    onCancel={() => setRenamingSmart(null)}
                  />
                </li>
              ) : (
                <SmartFolderRow
                  key={sf.id}
                  folder={sf}
                  count={smartCounts.get(sf.id) ?? 0}
                  onOpen={() => openSmart(sf)}
                  onRename={() => setRenamingSmart(sf.id)}
                  onDelete={() => mine.onDeleteSmartFolder(sf.id)}
                  quiet
                  onDownload={
                    mine.onDownloadMany && (smartCounts.get(sf.id) ?? 0) > 0
                      ? () => {
                          const f = { query: parseQuery(sf.query), chips: new Set(sf.chips as MaterialChip[]), favorites: allFavorites };
                          const clips = mineItems.filter((it) => matches(it, f) && it.payload !== null && "mine" in it.payload).map((it) => mineById.get((it.payload as { mine: string }).mine)).filter((c): c is MyClip => c !== undefined);
                          if (clips.length > 0) mine.onDownloadMany?.(clips, sf.name);
                        }
                      : undefined
                  }
                />
              ),
            )}
            {smartRows.length + folderRows.length > 0 || (filter === "mine" && !filtering && folderId === null && tagBrowse === null && tagIndex.length > 0) ? (
              !page && filter === "mine" && !filtering && folderId === null && tagBrowse === null && tagIndex.length > 0 && (
                <TagBrowseRow icon="tags" label="Tags" sub={`${tagIndex.reduce((n, g) => n + g.values.length, 0)} tags in ${tagIndex.length} group${tagIndex.length === 1 ? "" : "s"}`} onOpen={() => browseTags(null)} />
              )
            ) : null}
            {/* The review queue: what the browser guessed and nobody has answered.
                Tags and Needs review are rows on the shelf only: the page has
                them in its places (sidebar, or the phone's place menu). */}
            {!page && filter === "mine" && !filtering && folderId === null && tagBrowse === null && reviewCount > 0 && (
              <TagBrowseRow icon="review" label="Needs review" sub={`${reviewCount} clip${reviewCount === 1 ? "" : "s"} with suggestions waiting`} onOpen={openReview} />
            )}
            {shown.slice(0, rowLimit).map((item, index) => (
              <MaterialRow
                key={item.key}
                item={item}
                player={player}
                wave={index < WAVE_ROWS}
                columns={page && listWide}
                quiet
                active={item.key === activeKey}
                playing={item.playable !== null && player.auditioningId === item.playable.id}
                loading={item.playable !== null && player.auditionPendingId === item.playable.id}
                favorite={allFavorites.has(item.key)}
                target={target}
                where={item.payload ? (describeTarget?.(item.payload) ?? null) : null}
                inFolder={(filtering || tagBrowse !== null) && item.place === "mine" && item.folderId !== folderId && item.folderId !== null ? (folderName.get(item.folderId) ?? null) : null}
                onTagTap={item.place === "mine" ? (tag) => toggleChip(`tag:${tag}`) : undefined}
                chosenTags={chosenTags}
                suggestedPills={item.place === "mine" ? matchedSuggestions(item, { query: parsed, chips, favorites: allFavorites }) : []}
                canPlace={!page && item.payload !== null && (("material" in item.payload && onPlace !== undefined) || "mine" in item.payload || "mineMidi" in item.payload || ("reference" in item.payload && mine.signedIn))}
                canDownload={
                  item.payload !== null &&
                  (("material" in item.payload && onDownload !== undefined && (canDownload?.(item.payload.material) ?? true)) ||
                    ("mine" in item.payload && mine.onDownload !== undefined))
                }
                canSave={
                  mine.signedIn &&
                  item.payload !== null &&
                  ((item.place === "project" && "material" in item.payload && (item.kind === "midi" || mine.onSaveAudio !== undefined)) || (item.place === "seeds" && "reference" in item.payload))
                }
                onSwap={mine.swapTarget && mine.onSwap && item.place === "mine" && item.kind !== "midi" && item.payload !== null && "mine" in item.payload ? () => {
                  const clip = mineById.get((item.payload as { mine: string }).mine);
                  if (clip) mine.onSwap?.(clip);
                } : undefined}
                swapName={mine.swapTarget?.name ?? null}
                removing={item.payload !== null && (("mine" in item.payload && mine.removing === item.payload.mine) || ("mineMidi" in item.payload && mine.removing === item.payload.mineMidi))}
                selected={selected.has(item.key)}
                checkbox={selecting && item.place === "mine" && item.payload !== null}
                onSelect={(mod) => selectRow(item.key, mod)}
                onRename={item.place === "mine" && item.payload !== null ? (name) => mine.onRename(item.payload!, name) : undefined}
                usesOf={item.place === "mine" && item.uses > 0 && item.payload !== null && "mine" in item.payload ? () => mine.usesOf(mineClipId(item.payload)!) : undefined}
                onPreview={() => preview(item)}
                onPlace={() => place(item, "tap")}
                onFavorite={() => toggleFavorite(item.key)}
                onDownload={() => {
                  if (!item.payload) return;
                  if ("material" in item.payload) onDownload?.(item.payload.material);
                  else if ("mine" in item.payload) {
                    const clip = mineById.get(item.payload.mine);
                    if (clip) mine.onDownload?.(clip);
                  }
                }}
                onSave={() => {
                  if (!item.payload) return;
                  if ("material" in item.payload) {
                    if (item.kind === "midi") mine.onSaveMidi(item.payload.material, null);
                    else mine.onSaveAudio?.(item.payload.material, null);
                    onFilter("mine");
                  } else if ("reference" in item.payload) {
                    const ref = parseReference(item.payload.reference);
                    if (ref) mine.onKeepReference(ref.seedId, ref.audioKey, filter === "mine" ? folderId : null);
                  }
                }}
                onMove={item.place === "mine" && item.payload ? () => setMoving(selected.has(item.key) && tagTargets.length > 1 ? tagTargets.map((i) => i.payload!) : [item.payload!]) : undefined}
                onDelete={
                  item.payload && "mine" in item.payload
                    ? () => mine.onDelete((item.payload as { mine: string }).mine)
                    : item.payload && "mineMidi" in item.payload
                      ? () => mine.onDeleteMidi((item.payload as { mineMidi: string }).mineMidi)
                      : undefined
                }
                onLift={(lifting) => {
                  onLifting?.(lifting);
                  if (lifting) touch(item.key);
                }}
              />
            ))}
            {shown.length > rowLimit && (
              <li ref={moreRef} role="presentation" className="flex items-center justify-center py-3 text-[11px] text-muted-foreground">
                <button type="button" onClick={() => setRowLimit((n) => n + ROW_PAGE)} className="underline hover:text-foreground">
                  {shown.length - rowLimit} more
                </button>
              </li>
            )}
          </ul>
        )}
        {!page && filter === "mine" && mine.signedIn && (
          <p className="pt-2 text-center text-[11px] text-muted-foreground">
            {mine.usage ? `${mine.usage} · ` : ""}Drop audio files here to add them{folderId !== null ? " to this folder" : ""}. Only you see this list.
          </p>
        )}
      </div>
      {filter === "mine" && mine.signedIn && !inspect && tagTargets.length > 0 && (
        <QuickTags
          targets={tagTargets}
          complete={(prefix) => completeVocabulary(mineItems, prefix)}
          onSet={(tags) => tagDelta({ set: tags })}
          onUnset={(tags) => tagDelta({ unset: tags })}
          onAcceptTempo={(bpm) => tagTargets[0] && acceptTempo(tagTargets[0], bpm)}
          onReview={(keep, tags) => mine.onReview(tagTargets.map((i) => i.payload!), keep, tags)}
          swatch={tagTargets.every((i) => i.swatch === tagTargets[0]!.swatch) ? tagTargets[0]!.swatch : null}
          onColor={(value) => mine.onColor(tagTargets.map((i) => i.payload!), value)}
          onClear={() => {
            setSelected(new Set());
            setActiveKey(null);
          }}
        />
      )}
      {inspect &&
        inspectorTarget &&
        createPortal(
          <Inspector
            targets={filter === "mine" && mine.signedIn ? tagTargets : []}
            total={selectable.length}
            selecting={selecting}
            signedIn={mine.signedIn}
            player={player}
            folderName={folderName}
            confirmingDelete={confirmingDelete}
            starredAll={tagTargets.length > 0 && tagTargets.every((i) => allFavorites.has(i.key))}
            canDownload={mine.onDownload !== undefined}
            complete={(prefix) => completeVocabulary(mineItems, prefix)}
            onSet={(tags) => tagDelta({ set: tags })}
            onUnset={(tags) => tagDelta({ unset: tags })}
            onAcceptTempo={(bpm) => tagTargets[0] && acceptTempo(tagTargets[0], bpm)}
            onReview={(keep, tags) => mine.onReview(tagTargets.map((i) => i.payload!), keep, tags)}
            swatch={tagTargets.length > 0 && tagTargets.every((i) => i.swatch === tagTargets[0]!.swatch) ? tagTargets[0]!.swatch : null}
            onColor={(value) => mine.onColor(tagTargets.map((i) => i.payload!), value)}
            onRename={(name) => tagTargets[0]?.payload && mine.onRename(tagTargets[0].payload, name)}
            onSelectAll={selectAll}
            onClear={clearSelection}
            onDone={() => {
              setSelecting(false);
              clearSelection();
            }}
            onMove={() => setMoving(tagTargets.map((i) => i.payload!))}
            onStar={(starred) => {
              for (const it of tagTargets) if (it.payload) mine.onStar(it.payload, starred);
            }}
            onDownload={() => {
              const it = tagTargets[0];
              if (it?.payload && "mine" in it.payload) {
                const clip = mineById.get(it.payload.mine);
                if (clip) mine.onDownload?.(clip);
              }
            }}
            onDelete={() => setConfirmingDelete(true)}
            onDeleteConfirm={() => {
              for (const it of tagTargets) {
                if (!it.payload) continue;
                if ("mine" in it.payload) mine.onDelete(it.payload.mine);
                else if ("mineMidi" in it.payload) mine.onDeleteMidi(it.payload.mineMidi);
              }
              clearSelection();
            }}
            onDeleteCancel={() => setConfirmingDelete(false)}
          />,
          inspectorTarget,
        )}
      {moving && (
        <MovePicker
          folders={mine.folders}
          current={folderId}
          what={moving.length === 1 ? "it" : `${moving.length} clips`}
          onPick={(to) => moveTo(moving, to)}
          onClose={() => setMoving(null)}
        />
      )}
      {movingFolder && (
        <MovePicker
          folders={mine.folders}
          current={movingFolder.parentId}
          what={movingFolder.name}
          allowed={(to) => canMoveFolder(mine.folders, movingFolder.id, to, LIBRARY_FOLDER_MAX_DEPTH)}
          onPick={(to) => moveFolderTo(movingFolder.id, to)}
          onClose={() => setMovingFolder(null)}
        />
      )}
    </div>
  );
}

const KIND_TAGS = new Set(["loop", "oneshot", "midi", "session", "drums", "bass", "keys", "vocals", "instruments", "fx"]);

/** What the shelf shows of an upload in flight: a row per file, the bytes, the clock. */
export interface UploadProgressView {
  files: ClipFileView[];
  stage: "listening" | "converting" | "sending" | "checking" | "failed";
  done?: number;
  total?: number;
  sentBytes: number;
  totalBytes: number;
  /** When the first byte went over the wire; null before. */
  sendingSince: number | null;
  failed: number;
  active: boolean;
}

/** Rows shown before the list folds the rest into "+n more". */
const PROGRESS_ROWS = 6;

/** "340 KB", "2.4 MB", "40 MB" — an upload's sizes, where a clip is often under a megabyte. */
function formatSize(b: number): string {
  if (b < 1024 * 1024) return `${Math.max(1, Math.round(b / 1024))} KB`;
  const mb = b / (1024 * 1024);
  return mb < 10 ? `${mb.toFixed(1)} MB` : `${Math.round(mb)} MB`;
}

/**
 * An upload, file by file: one bar for the whole, "12 MB of 40 MB · About
 * 20 s left", then a row per file with what it is doing — and, for one that
 * failed, the put's own words. Files already added fold away first.
 */
export function UploadProgress({ view }: { view: UploadProgressView }) {
  // The clock for the time-left estimate: a tick a second while bytes move.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (view.stage !== "sending" && view.stage !== "checking") return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [view.stage]);
  const elapsedSec = view.sendingSince === null ? 0 : Math.max(0, (now - view.sendingSince) / 1000);
  const stage: ProgressStage | null =
    view.stage === "listening"
      ? { kind: "preparing", fraction: 0.3 }
      : view.stage === "converting"
        ? { kind: "preparing", fraction: 0.3 + 0.7 * ((view.done ?? 0) / Math.max(1, view.total ?? 1)) }
        : view.stage === "sending"
          ? { kind: "sending", sentBytes: view.sentBytes, totalBytes: view.totalBytes, elapsedSec }
          : view.stage === "checking"
            ? { kind: "checking", totalBytes: view.totalBytes, elapsedSec }
            : null;
  const overall = stage ? overallProgress(stage) : 0;
  const left = stage && view.active ? timeLeftText(secondsLeft(stage)) : null;
  // What is still moving or went wrong first; what landed folds away.
  const order = (v: ClipFileView): number => (v.status === "failed" ? 0 : v.status === "sending" ? 1 : v.status === "added" || v.status === "already" ? 3 : 2);
  const rows = [...view.files].sort((a, b) => order(a) - order(b));
  const shown = rows.slice(0, PROGRESS_ROWS);
  const landed = view.files.filter((v) => v.status === "added" || v.status === "already" || v.status === "sent").length;
  return (
    <div className="mt-1.5 rounded-lg border border-panelline bg-panel/70 px-2 py-1.5 text-[11px]" aria-label="Upload progress">
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
        <div
          className={cn("h-full rounded-full transition-all duration-500 ease-out", view.failed > 0 && !view.active ? "bg-removed" : "bg-primary")}
          style={{ width: `${Math.max(3, Math.round(overall * 100))}%` }}
        />
      </div>
      <p className="mt-1 flex justify-between gap-2 text-muted-foreground">
        <span className="min-w-0 truncate">
          {view.totalBytes > 0 ? `${view.sentBytes > 0 ? formatSize(view.sentBytes) : "0 KB"} of ${formatSize(view.totalBytes)}` : `${view.files.length} file${view.files.length === 1 ? "" : "s"}`}
          {landed > 0 && ` · ${landed} of ${view.files.length} up`}
        </span>
        {left && <span className="shrink-0">{left}</span>}
      </p>
      <ul className="mt-1 flex flex-col gap-0.5">
        {shown.map((v) => (
          <li key={v.id} className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate">{v.label}</span>
            {v.status === "sending" && v.bytes > 0 && (
              <span className="h-1 w-16 shrink-0 overflow-hidden rounded-full bg-white/10">
                <span className="block h-full rounded-full bg-primary" style={{ width: `${Math.max(2, Math.round((v.sent / v.bytes) * 100))}%` }} />
              </span>
            )}
            <span className={cn("max-w-[60%] shrink-0 truncate text-right", v.status === "failed" ? "text-removed" : v.status === "added" ? "text-primary" : "text-muted-foreground")} title={v.reason}>
              {clipStatusText(v)}
            </span>
          </li>
        ))}
        {rows.length > shown.length && <li className="text-muted-foreground">+{rows.length - shown.length} more</li>}
      </ul>
    </div>
  );
}

/**
 * Live's Quick Tags: the tags of what is selected, the browser's suggestions
 * waiting on a ✓, and Add…. With several rows picked, a tag not on every one
 * of them wears Live's `*`; setting it puts it on all.
 */
function QuickTags({
  targets,
  complete,
  onSet,
  onUnset,
  onAcceptTempo,
  onReview,
  onClear,
  swatch,
  onColor,
  layout = "footer",
}: {
  targets: MaterialItem[];
  /** `footer`: the shelf's strip under the list (name, then one scrolling
   * row). `panel`: inside the inspector — no name (the panel has it), rows
   * wrap, completions open downward. */
  layout?: "footer" | "panel";
  /** The colour every selected row carries (null: none, or not the same). */
  swatch: number | null;
  /** Live's Collections: a colour on the selection, or none. */
  onColor: (value: number | null) => void;
  complete: (prefix: string) => TagCompletion[];
  onSet: (tags: string[]) => void;
  onUnset: (tags: string[]) => void;
  onAcceptTempo: (bpm: number) => void;
  /** The waiting suggestions of the selection, kept or dismissed — all, or the named tags. */
  onReview: (keep: boolean, tags?: readonly string[]) => void;
  onClear: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState("");
  const [cursor, setCursor] = useState(0);
  const targetKeys = targets.map((t) => t.key).join("|");
  useEffect(() => {
    setAdding(false);
    setText("");
  }, [targetKeys]);

  // Confirmed tags across the selection, with how many rows carry each.
  const stored = new Map<string, number>();
  for (const t of targets) for (const tag of t.tags) if (!KIND_TAGS.has(tag)) stored.set(tag, (stored.get(tag) ?? 0) + 1);
  const confirmed = sortTags([...stored.keys()]);
  const suggested = targets.length === 1 ? sortTags(targets[0]!.suggested) : [];
  // Several rows: the union of what the browser suggested, with how many
  // rows each sits on — one tap keeps a tag on every row that suggested it.
  const union = new Map<string, number>();
  if (targets.length > 1) for (const t of targets) for (const tag of t.suggested) union.set(tag, (union.get(tag) ?? 0) + 1);
  const unionTags = sortTags([...union.keys()]);
  const waiting = targets.reduce((n, t) => n + (t.suggested.length > 0 ? 1 : 0), 0);
  const completions = adding ? complete(text) : [];
  const commit = (c: TagCompletion | null): void => {
    const tag = c ? c.tag : normalizeTag(text);
    if (tag === "" || !validTag(tag)) return;
    onSet([tag]);
    setText("");
    setCursor(0);
  };
  const name = targets.length === 1 ? cleanClipName(targets[0]!.name) || "(unnamed)" : `${targets.length} selected`;

  return (
    <div className={cn("relative", layout === "footer" ? "shrink-0 border-t border-panelline/60 bg-sunk px-3 py-2" : "")} aria-label="Tags">
      {adding && completions.length > 0 && (
        <ul role="listbox" aria-label="Tag suggestions" className={cn("absolute z-20 w-60 overflow-hidden rounded-lg border border-panelline bg-raised shadow-lg", layout === "footer" ? "bottom-full left-3 mb-1" : "left-0 top-full mt-1")}>
          {completions.map((c, i) => (
            <li key={c.tag}>
              <button
                type="button"
                role="option"
                aria-selected={i === cursor}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(c)}
                className={cn("flex h-9 w-full items-center gap-2 px-3 text-left text-xs hover:bg-white/10 wide:h-7", i === cursor && "bg-white/10")}
              >
                <span className="truncate">{c.free ? `${c.label} as a new tag` : c.label}</span>
                {c.group && <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">{c.group}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {layout === "panel" ? (
        <div className="flex items-center gap-2">
          <Tag className="size-3 shrink-0 text-muted-foreground" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Tags</span>
          {targets.length > 1 && unionTags.length > 0 && (
            <span className="ml-auto flex shrink-0 items-center gap-1">
              <button type="button" onClick={() => onReview(true)} className="flex h-7 items-center gap-1 rounded-full border border-primary/50 px-2.5 text-[11px] font-semibold text-primary hover:bg-primary/10" title={`Keep every suggestion on the ${waiting} row${waiting === 1 ? "" : "s"} that have one`}>
                <Check className="size-3" /> Keep all
              </button>
              <button type="button" onClick={() => onReview(false)} className="flex h-7 items-center gap-1 rounded-full border border-panelline px-2.5 text-[11px] font-semibold text-muted-foreground hover:text-foreground" title="Drop every waiting suggestion on the selection">
                <X className="size-3" /> Dismiss all
              </button>
            </span>
          )}
        </div>
      ) : (
      <div className="flex items-center gap-2">
        <Tag className="size-3 shrink-0 text-muted-foreground" />
        <span className="min-w-0 truncate text-[11px] font-semibold" title={name}>
          {name}
        </span>
        {targets.length > 1 && unionTags.length > 0 && (
          <span className="ml-auto flex shrink-0 items-center gap-1">
            <button type="button" onClick={() => onReview(true)} className="flex h-7 items-center gap-1 rounded-full border border-primary/50 px-2.5 text-[11px] font-semibold text-primary hover:bg-primary/10 wide:h-6" title={`Keep every suggestion on the ${waiting} row${waiting === 1 ? "" : "s"} that have one`}>
              <Check className="size-3" /> Keep all
            </button>
            <button type="button" onClick={() => onReview(false)} className="flex h-7 items-center gap-1 rounded-full border border-panelline px-2.5 text-[11px] font-semibold text-muted-foreground hover:text-foreground wide:h-6" title="Drop every waiting suggestion on the selection">
              <X className="size-3" /> Dismiss all
            </button>
          </span>
        )}
        <button type="button" onClick={onClear} aria-label="Done tagging" className={cn("flex size-7 items-center justify-center rounded-md text-muted-foreground hover:text-foreground", !(targets.length > 1 && unionTags.length > 0) && "ml-auto")}>
          <X className="size-3.5" />
        </button>
      </div>
      )}
      <div className="mt-1.5 flex items-center gap-1" role="radiogroup" aria-label="Colour">
        {SWATCHES.map((hex, i) => (
          <button
            key={hex}
            type="button"
            role="radio"
            aria-checked={swatch === i}
            aria-label={`Colour ${i + 1}`}
            onClick={() => onColor(swatch === i ? null : i)}
            className={cn("flex size-7 items-center justify-center rounded-full wide:size-5", swatch === i ? "ring-2 ring-foreground/70" : "hover:ring-1 hover:ring-foreground/40")}
          >
            <span className="size-3.5 rounded-full wide:size-3" style={{ backgroundColor: hex }} />
          </button>
        ))}
        {swatch !== null && (
          <button type="button" onClick={() => onColor(null)} className="ml-1 text-[10px] text-muted-foreground hover:text-foreground">
            No colour
          </button>
        )}
      </div>
      <div className={cn("mt-1.5 flex items-center gap-1.5 pb-0.5", layout === "footer" ? "scrollbar-none overflow-x-auto" : "flex-wrap")}>
        {confirmed.map((tag) => {
          const partial = (stored.get(tag) ?? 0) < targets.length;
          return (
            <span key={tag} className="flex h-8 shrink-0 items-center gap-1 rounded-full border border-foreground/30 bg-white/10 pl-2.5 pr-1 text-[11px] font-semibold wide:h-6" title={partial ? "On some of the selected rows — tap to put it on all" : parseTag(tag).group ?? "Your tag"}>
              {partial ? (
                <button type="button" onClick={() => onSet([tag])} className="hover:text-primary">
                  {tagLabel(tag)}*
                </button>
              ) : (
                tagLabel(tag)
              )}
              <button type="button" onClick={() => onUnset([tag])} aria-label={`Remove ${tagLabel(tag)}`} className="flex size-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground wide:size-5">
                <X className="size-3" />
              </button>
            </span>
          );
        })}
        {suggested.map((tag) => {
          const tempo = parseTag(tag).group === "tempo" ? Number(parseTag(tag).value) : null;
          return (
            <span key={tag} className="flex h-8 shrink-0 items-center gap-0.5 rounded-full border border-dashed border-primary/50 pl-2.5 pr-1 text-[11px] text-muted-foreground wide:h-6" title="Suggested — tap ✓ to keep it">
              {tagLabel(tag)}
              <button
                type="button"
                onClick={() => (tempo !== null ? onAcceptTempo(tempo) : onSet([tag]))}
                aria-label={`Keep ${tagLabel(tag)}`}
                className="flex size-6 items-center justify-center rounded-full text-primary hover:bg-primary/15 wide:size-5"
              >
                <Check className="size-3" />
              </button>
              <button type="button" onClick={() => onUnset([tag])} aria-label={`Dismiss ${tagLabel(tag)}`} className="flex size-6 items-center justify-center rounded-full hover:text-foreground wide:size-5">
                <X className="size-3" />
              </button>
            </span>
          );
        })}
        {unionTags.map((tag) => {
          const tempo = parseTag(tag).group === "tempo";
          return (
            <span key={`u:${tag}`} className="flex h-8 shrink-0 items-center gap-0.5 rounded-full border border-dashed border-primary/50 pl-2.5 pr-1 text-[11px] text-muted-foreground wide:h-6" title={`Suggested on ${union.get(tag)} of ${targets.length} — ✓ keeps it on those`}>
              {tagLabel(tag)}
              <span className="ml-1 font-mono text-[9px] opacity-70">{union.get(tag)}</span>
              {!tempo && (
                <button type="button" onClick={() => onReview(true, [tag])} aria-label={`Keep ${tagLabel(tag)} on ${union.get(tag)} rows`} className="flex size-6 items-center justify-center rounded-full text-primary hover:bg-primary/15 wide:size-5">
                  <Check className="size-3" />
                </button>
              )}
              <button type="button" onClick={() => onReview(false, [tag])} aria-label={`Dismiss ${tagLabel(tag)} on ${union.get(tag)} rows`} className="flex size-6 items-center justify-center rounded-full hover:text-foreground wide:size-5">
                <X className="size-3" />
              </button>
            </span>
          );
        })}
        {adding ? (
          <span className="relative shrink-0">
            <input
              autoFocus
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setCursor(0);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commit(completions[cursor] ?? null);
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setAdding(false);
                  setText("");
                } else if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setCursor((c) => Math.min(completions.length - 1, c + 1));
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setCursor((c) => Math.max(0, c - 1));
                } else if (e.key === "Tab" && completions[0]) {
                  e.preventDefault();
                  commit(completions[cursor] ?? completions[0]);
                }
              }}
              onBlur={() => {
                if (text.trim() === "") setAdding(false);
              }}
              placeholder="Add a tag"
              aria-label="Add a tag"
              maxLength={40}
              className="h-8 w-36 rounded-full border border-controlline bg-control px-3 text-[11px] outline-none wide:h-6"
            />
          </span>
        ) : (
          <button
            type="button"
            id={layout === "panel" ? "library-add-tag" : undefined}
            onClick={() => setAdding(true)}
            title="Add a tag (⌘E)"
            className="flex h-8 shrink-0 items-center gap-1 rounded-full border border-panelline px-2.5 text-[11px] font-semibold text-muted-foreground hover:text-foreground wide:h-6"
          >
            <Plus className="size-3" />
            Add…
          </button>
        )}
        {confirmed.length === 0 && suggested.length === 0 && unionTags.length === 0 && !adding && <span className="shrink-0 text-[11px] text-muted-foreground">No tags yet.</span>}
      </div>
    </div>
  );
}

/**
 * What a selection can do, all at once: the count (and whether that is every
 * row shown), Select all, Clear, Move to…, Star, Delete with the count to
 * confirm. Tags are the Quick Tags footer, which already follows the
 * selection. Done ends the phone's checkbox mode.
 */
function SelectionBar({
  count,
  total,
  selecting,
  starredAll,
  confirmingDelete,
  onSelectAll,
  onClear,
  onDone,
  onMove,
  onStar,
  onDelete,
  onDeleteConfirm,
  onDeleteCancel,
}: {
  count: number;
  total: number;
  selecting: boolean;
  starredAll: boolean;
  confirmingDelete: boolean;
  onSelectAll: () => void;
  onClear: () => void;
  onDone: () => void;
  onMove: () => void;
  onStar: (starred: boolean) => void;
  onDelete: () => void;
  onDeleteConfirm: () => void;
  onDeleteCancel: () => void;
}) {
  const action = "flex h-8 shrink-0 items-center gap-1 rounded-md px-2 text-[11px] font-semibold text-muted-foreground hover:bg-white/10 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40 wide:h-6";
  return (
    <div className="mt-2 flex items-center gap-1 rounded-lg border border-primary/40 bg-primary/10 px-2 py-1" role="toolbar" aria-label="Selection">
      <span className="shrink-0 whitespace-nowrap text-[11px] font-semibold" aria-live="polite">
        {count === 0 ? "Tap rows to select" : `${count} selected${total > 0 && count === total ? " (all shown)" : ""}`}
      </span>
      {count < total && (
        <button type="button" onClick={onSelectAll} className={cn(action, "min-w-0 truncate")} title="Every row shown (⌘A)">
          Select all {total}
        </button>
      )}
      {count > 0 && (
        <button type="button" onClick={onClear} className={action}>
          Clear
        </button>
      )}
      <span className="ml-auto" />
      {confirmingDelete ? (
        <span className="flex shrink-0 items-center gap-1 text-[11px]">
          <button type="button" onClick={onDeleteConfirm} className="h-8 rounded-md bg-removed/20 px-2 font-semibold text-removed hover:bg-removed/30 wide:h-6">
            Delete {count}
          </button>
          <button type="button" onClick={onDeleteCancel} className="h-8 rounded-md px-2 text-muted-foreground hover:text-foreground wide:h-6">
            Keep
          </button>
        </span>
      ) : (
        <>
          <button type="button" onClick={onMove} disabled={count === 0} className={action} title="Move the selection to a folder">
            <FolderInput className="size-3.5" />
            <span className="hidden sm:inline">Move to…</span>
          </button>
          <button type="button" onClick={() => onStar(!starredAll)} disabled={count === 0} className={action} title={starredAll ? "Unstar the selection" : "Star the selection"}>
            <Star className={cn("size-3.5", starredAll && "fill-current text-primary")} />
            <span className="hidden sm:inline">{starredAll ? "Unstar" : "Star"}</span>
          </button>
          <button type="button" onClick={onDelete} disabled={count === 0} className={action} title="Delete the selection from your library (projects that use them keep them)">
            <Trash2 className="size-3.5" />
            <span className="hidden sm:inline">Delete</span>
          </button>
        </>
      )}
      {selecting && (
        <button type="button" onClick={onDone} className="flex h-8 shrink-0 items-center rounded-md bg-white/10 px-2.5 text-[11px] font-semibold text-foreground hover:bg-white/15 wide:h-6">
          Done
        </button>
      )}
    </div>
  );
}

/** Mine › Drums › Breaks — where the rows on screen live; each crumb opens
 * that folder, and the ‹ is the way back on a phone. */
function PathLine({
  path,
  smart = null,
  tagTrail = null,
  onOpen,
  onNew,
  canNew,
  dropTarget,
  dropFolder,
}: {
  path: MyFolder[];
  /** The smart folder open, shown as the last crumb. */
  smart?: MySmartFolder | null;
  /** Browsing by tag: Mine › Tags › Drums › Kick. */
  tagTrail?: { group: TagIndexGroup | null; tag: string | null; onGroups: () => void; onGroup: () => void } | null;
  onOpen: (id: string | null) => void;
  onNew: () => void;
  canNew: boolean;
  dropTarget: (to: string | null) => { onDragOver: (e: DragEvent) => void; onDragLeave: () => void; onDrop: (e: DragEvent) => void };
  dropFolder: string | null;
}) {
  const crumbs: Array<{ id: string | null; name: string; smart?: boolean; go?: () => void }> = [
    { id: null, name: "Mine" },
    ...path.map((f) => ({ id: f.id, name: f.name })),
    ...(smart ? [{ id: null, name: smart.name, smart: true }] : []),
    ...(tagTrail
      ? [
          { id: null, name: "Tags", go: tagTrail.onGroups },
          ...(tagTrail.group ? [{ id: null, name: tagTrail.group.label, go: tagTrail.onGroup }] : []),
          ...(tagTrail.tag ? [{ id: null, name: tagLabel(tagTrail.tag), go: () => {} }] : []),
        ]
      : []),
  ];
  const up = crumbs[crumbs.length - 2];
  return (
    <nav aria-label="Folder" className="mt-2 flex min-w-0 items-center gap-1">
      {up !== undefined && (
        <button
          type="button"
          onClick={() => (up.go ? up.go() : onOpen(up.id))}
          aria-label={`Back to ${up.name}`}
          title={`Back to ${up.name}`}
          className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-panelline text-muted-foreground hover:text-foreground wide:size-7"
        >
          <ChevronLeft className="size-4" />
        </button>
      )}
      <ol className="scrollbar-none flex min-w-0 flex-1 items-center overflow-x-auto text-xs">
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1;
          return (
            <li key={c.smart ? "smart" : c.go ? `tag-${c.name}` : (c.id ?? "root")} className="flex shrink-0 items-center">
              {i > 0 && <ChevronRight className="mx-0.5 size-3 shrink-0 text-muted-foreground/60" />}
              <button
                type="button"
                onClick={() => (c.go ? c.go() : c.smart ? undefined : onOpen(c.id))}
                aria-current={last ? "location" : undefined}
                {...(c.smart || c.go ? {} : dropTarget(c.id))}
                className={cn(
                  "flex max-w-[9rem] items-center gap-1 truncate rounded-md px-1.5 py-1",
                  last ? "font-semibold text-foreground" : "text-muted-foreground hover:text-foreground",
                  dropFolder === c.id && !last && !c.smart && "bg-primary/20 text-foreground",
                )}
              >
                {c.smart && <Bookmark className="size-3 shrink-0 text-primary" />}
                {c.go && i === crumbs.length - (tagTrail?.tag ? 3 : tagTrail?.group ? 2 : 1) && <Tags className="size-3 shrink-0 text-muted-foreground" />}
                {c.name}
              </button>
            </li>
          );
        })}
      </ol>
      <button
        type="button"
        onClick={onNew}
        disabled={!canNew}
        title={canNew ? "New folder here" : `Folders go ${LIBRARY_FOLDER_MAX_DEPTH} deep at most`}
        aria-label="New folder"
        className="flex h-9 shrink-0 items-center gap-1 rounded-lg border border-panelline px-2 text-[11px] font-semibold text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40 wide:h-7"
      >
        <FolderPlus className="size-3.5" />
        <span className="hidden sm:inline">Folder</span>
      </button>
    </nav>
  );
}

/** A folder in the list: open it, or drop a row on it. */
function FolderRow({
  folder,
  count,
  bytes,
  over,
  dropProps,
  onOpen,
  onRename,
  onMove,
  onDragFolder,
  onDelete,
  suggestTags,
  onSuggest,
  quiet = false,
}: {
  folder: MyFolder;
  count: number;
  /** Stored bytes in it and under it; 0 says nothing. */
  bytes: number;
  over: boolean;
  dropProps: { onDragOver: (e: DragEvent) => void; onDragLeave: () => void; onDrop: (e: DragEvent) => void };
  onOpen: () => void;
  onRename: () => void;
  /** The whole folder to the Move to… picker. */
  onMove: () => void;
  /** The row picked up (true) or put down (false) by a mouse drag. */
  onDragFolder: (dragging: boolean) => void;
  onDelete: () => void;
  /** What the folder's name says, as tags — the Suggest button's promise; none hides it. */
  suggestTags: readonly string[];
  /** Write those as suggestions on everything in the folder and under it. */
  onSuggest: () => void;
  /** The page: the buttons wait for a hover. */
  quiet?: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const [suggested, setSuggested] = useState(false);
  return (
    <li
      data-folder={folder.id}
      {...dropProps}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData(MINE_FOLDER_DRAG_TYPE, folder.id);
        e.dataTransfer.effectAllowed = "move";
        onDragFolder(true);
      }}
      onDragEnd={() => onDragFolder(false)}
      className={cn(
        TOUCH,
        "group flex min-h-11 cursor-grab items-center gap-2 rounded-lg border px-2 py-1 transition-colors wide:min-h-9",
        over ? "border-primary bg-primary/10" : "border-panelline bg-panel/70 hover:bg-white/[0.04]",
      )}
      title={`${folder.name} — drag onto a folder to move it there`}
    >
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
        aria-label={`Open ${folder.name}`}
      >
        <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-panelline text-muted-foreground wide:size-7" style={folder.color ? { color: folder.color } : undefined}>
          <Folder className="size-3.5" />
        </span>
        <span className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="min-w-0 flex-1 truncate text-xs font-semibold">{folder.name}</span>
          <span className="max-w-[45%] shrink-0 truncate text-[10px] text-muted-foreground">
            {count === 0 ? "Empty" : `${count} clip${count === 1 ? "" : "s"}`}
            {bytes > 0 && ` · ${formatMb(bytes)}`}
          </span>
        </span>
      </button>
      {confirming ? (
        <span className="flex shrink-0 items-center gap-1 text-[11px]">
          <span className="hidden text-muted-foreground sm:inline">{count > 0 ? "Clips move up a level." : ""}</span>
          <button
            type="button"
            onClick={() => {
              setConfirming(false);
              onDelete();
            }}
            className="h-8 rounded-md bg-removed/20 px-2 font-semibold text-removed hover:bg-removed/30"
          >
            Delete
          </button>
          <button type="button" onClick={() => setConfirming(false)} className="h-8 rounded-md px-2 text-muted-foreground hover:text-foreground">
            Keep
          </button>
        </span>
      ) : (
        <span className={cn("flex shrink-0 items-center gap-0.5", quiet && "hidden group-focus-within:flex group-hover:flex")}>
          {suggestTags.length > 0 && count > 0 && (
            <button
              type="button"
              onClick={() => {
                onSuggest();
                setSuggested(true);
                window.setTimeout(() => setSuggested(false), 4000);
              }}
              title={suggested ? "Suggested — find them under Needs review" : `Suggest ${suggestTags.map(tagLabel).join(", ")} on everything in here, from the folder's name`}
              aria-label={`Suggest tags from the name of ${folder.name}`}
              className={cn("flex size-10 shrink-0 items-center justify-center rounded-lg wide:size-7", suggested ? "text-primary" : "text-muted-foreground hover:bg-white/10 hover:text-foreground")}
            >
              {suggested ? <Check className="size-3.5" /> : <Sparkles className="size-3.5" />}
            </button>
          )}
          <button
            type="button"
            onClick={onRename}
            title="Rename"
            aria-label={`Rename ${folder.name}`}
            className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-white/10 hover:text-foreground wide:size-7"
          >
            <Pencil className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={onMove}
            title="Move the folder, and everything in it"
            aria-label={`Move ${folder.name} to a folder`}
            className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-white/10 hover:text-foreground wide:size-7"
          >
            <FolderInput className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setConfirming(true)}
            title={count > 0 ? "Delete the folder (its clips move up a level)" : "Delete the folder"}
            aria-label={`Delete ${folder.name}`}
            className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-white/10 hover:text-foreground wide:size-7"
          >
            <Trash2 className="size-3.5" />
          </button>
        </span>
      )}
      <button
        type="button"
        onClick={onOpen}
        title="Open"
        aria-hidden
        tabIndex={-1}
        className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground wide:size-7"
      >
        <ChevronRight className="size-4" />
      </button>
    </li>
  );
}

/** A row that opens a tag group or a tag's clips — a folder that WHAT made. */
function TagBrowseRow({ icon, label, sub, onOpen, onRename }: { icon: "tags" | "tag" | "hash" | "review"; label: string; sub: string; onOpen: () => void; onRename?: () => void }) {
  const Icon = icon === "tags" ? Tags : icon === "hash" ? Hash : icon === "review" ? Sparkles : Tag;
  return (
    <li className={cn(TOUCH, "group flex min-h-11 items-center gap-2 rounded-lg border border-panelline bg-panel/70 px-2 py-1 transition-colors hover:bg-white/[0.04] wide:min-h-9")}>
      <button type="button" onClick={onOpen} className="flex min-w-0 flex-1 items-center gap-2 text-left" aria-label={`Open ${label}`}>
        <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-panelline text-muted-foreground wide:size-7">
          <Icon className="size-3.5" />
        </span>
        <span className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="min-w-0 flex-1 truncate text-xs font-semibold">{label}</span>
          <span className="max-w-[55%] shrink-0 truncate text-[10px] text-muted-foreground">{sub}</span>
        </span>
      </button>
      {onRename && (
        <button
          type="button"
          onClick={onRename}
          title="Rename this tag everywhere — or type a tag to merge it into"
          aria-label={`Rename ${label}`}
          className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-white/10 hover:text-foreground wide:size-7"
        >
          <Pencil className="size-3.5" />
        </button>
      )}
      <button type="button" onClick={onOpen} aria-hidden tabIndex={-1} className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground wide:size-7">
        <ChevronRight className="size-4" />
      </button>
    </li>
  );
}

/** A saved search in the list: open it; rename or drop it. Never a drop target. */
function SmartFolderRow({ folder, count, onOpen, onRename, onDelete, onDownload, quiet = false }: { folder: MySmartFolder; count: number; onOpen: () => void; onRename: () => void; onDelete: () => void; onDownload?: () => void; quiet?: boolean }) {
  const [confirming, setConfirming] = useState(false);
  const what = [folder.query, ...folder.chips.map((c) => (c.startsWith("tag:") ? `#${c.slice(4)}` : c === "favorite" ? "★" : c))].filter((s) => s !== "").join(" · ");
  return (
    <li data-smart={folder.id} className={cn(TOUCH, "group flex min-h-11 items-center gap-2 rounded-lg border border-dashed border-primary/40 bg-panel/70 px-2 py-1 transition-colors hover:bg-white/[0.04] wide:min-h-9")}>
      <button type="button" onClick={onOpen} className="flex min-w-0 flex-1 items-center gap-2 text-left" aria-label={`Open ${folder.name}`}>
        <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-panelline text-primary wide:size-7" style={folder.color ? { color: folder.color } : undefined}>
          <Bookmark className="size-3.5" />
        </span>
        <span className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="min-w-0 flex-1 truncate text-xs font-semibold">{folder.name}</span>
          <span className="max-w-[55%] shrink-0 truncate text-[10px] text-muted-foreground" title={what}>
            {count === 0 ? "Nothing matches" : `${count} clip${count === 1 ? "" : "s"}`}
            {what ? ` · ${what}` : ""}
          </span>
        </span>
      </button>
      {confirming ? (
        <span className="flex shrink-0 items-center gap-1 text-[11px]">
          <span className="hidden text-muted-foreground sm:inline">Only the saved search goes.</span>
          <button
            type="button"
            onClick={() => {
              setConfirming(false);
              onDelete();
            }}
            className="h-8 rounded-md bg-removed/20 px-2 font-semibold text-removed hover:bg-removed/30"
          >
            Delete
          </button>
          <button type="button" onClick={() => setConfirming(false)} className="h-8 rounded-md px-2 text-muted-foreground hover:text-foreground">
            Keep
          </button>
        </span>
      ) : (
        <span className={cn("flex shrink-0 items-center gap-0.5", quiet && "hidden group-focus-within:flex group-hover:flex")}>
          {onDownload && (
            <button type="button" onClick={onDownload} title={`Download the ${count} clip${count === 1 ? "" : "s"} this search finds, as a zip`} aria-label={`Download ${folder.name}`} className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-white/10 hover:text-foreground wide:size-7">
              <Download className="size-3.5" />
            </button>
          )}
          <button type="button" onClick={onRename} title="Rename" aria-label={`Rename ${folder.name}`} className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-white/10 hover:text-foreground wide:size-7">
            <Pencil className="size-3.5" />
          </button>
          <button type="button" onClick={() => setConfirming(true)} title="Delete the smart folder (its clips stay)" aria-label={`Delete ${folder.name}`} className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-white/10 hover:text-foreground wide:size-7">
            <Trash2 className="size-3.5" />
          </button>
          <button type="button" onClick={onOpen} title="Open" aria-hidden tabIndex={-1} className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground wide:size-7">
            <ChevronRight className="size-4" />
          </button>
        </span>
      )}
    </li>
  );
}

/** Naming a new folder, or renaming one: a row with a box in it. */
function FolderNameRow({ initial = "", placeholder, smart = false, kind = smart ? "smart" : "folder", onDone, onCancel }: { initial?: string; placeholder: string; smart?: boolean; kind?: "folder" | "smart" | "tag"; onDone: (name: string) => void; onCancel: () => void }) {
  const [value, setValue] = useState(initial);
  const commit = (): void => {
    const name = value.trim();
    if (name === "") onCancel();
    else onDone(name);
  };
  return (
    <div className="flex min-h-11 items-center gap-2 rounded-lg border border-controlline bg-panel px-2 py-1 wide:min-h-9">
      <span className={cn("flex size-8 shrink-0 items-center justify-center rounded-md border border-panelline wide:size-7", kind === "smart" ? "text-primary" : "text-muted-foreground")}>
        {kind === "smart" ? <Bookmark className="size-3.5" /> : kind === "tag" ? <Hash className="size-3.5" /> : <Folder className="size-3.5" />}
      </span>
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            onCancel();
          }
        }}
        placeholder={placeholder}
        aria-label={placeholder}
        maxLength={120}
        className="h-9 min-w-0 flex-1 rounded-lg border border-panelline bg-control px-2 text-xs outline-none focus:border-controlline wide:h-7"
      />
      <button type="button" onClick={commit} aria-label="Done" className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-controlline bg-control text-foreground/80 hover:text-foreground wide:size-7">
        <Check className="size-4" />
      </button>
      <button type="button" onClick={onCancel} aria-label="Cancel" className="flex size-10 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground wide:size-7">
        <X className="size-4" />
      </button>
    </div>
  );
}

/** Move to… — the tree, flattened with its depth shown, one tap per folder. */
function MovePicker({
  folders,
  current,
  what,
  allowed,
  onPick,
  onClose,
}: {
  folders: MyFolder[];
  current: string | null;
  /** What is moving, as the title says it: "it", "3 clips", a folder's name. */
  what: string;
  /** Targets to offer; the rest stay listed but greyed (a folder cannot go
   * into itself, or deeper than folders go). Default: anywhere but here. */
  allowed?: (to: string | null) => boolean;
  onPick: (to: string | null) => void;
  onClose: () => void;
}) {
  const rows: Array<{ id: string | null; name: string; depth: number }> = [{ id: null, name: "Mine", depth: 0 }];
  const walk = (parentId: string | null, depth: number): void => {
    for (const f of childFolders(folders, parentId)) {
      rows.push({ id: f.id, name: f.name, depth });
      walk(f.id, depth + 1);
    }
  };
  walk(null, 1);
  return (
    <div role="dialog" aria-label="Move to" className="absolute inset-x-3 bottom-3 z-30 max-h-[60%] overflow-hidden rounded-xl border border-panelline bg-raised shadow-xl">
      <div className="flex items-center justify-between border-b border-panelline px-3 py-2 text-xs">
        <span className="min-w-0 truncate font-semibold">Move {what} to</span>
        <button type="button" onClick={onClose} aria-label="Close" className="flex size-8 items-center justify-center rounded-md text-muted-foreground hover:text-foreground">
          <X className="size-4" />
        </button>
      </div>
      <ul className="max-h-64 overflow-auto py-1">
        {rows.map((r) => (
          <li key={r.id ?? "root"}>
            <button
              type="button"
              onClick={() => onPick(r.id)}
              disabled={r.id === current || (allowed !== undefined && !allowed(r.id))}
              className={cn(
                "flex h-10 w-full items-center gap-2 px-3 text-left text-xs hover:bg-white/10 disabled:opacity-40 wide:h-8",
              )}
              style={{ paddingLeft: `${12 + r.depth * 14}px` }}
            >
              <Folder className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate">{r.name}</span>
              {r.id === current && <span className="ml-auto text-[10px] text-muted-foreground">here</span>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Project | Mine | Seeds | Recent — where the rows come from. */
function PlaceSwitch({ value, onChange, recentCount, seedCount }: { value: MaterialPlace; onChange: (f: MaterialPlace) => void; recentCount: number; seedCount: number }) {
  const option = (f: MaterialPlace, label: string, disabled = false, title?: string) => (
    <button
      key={f}
      type="button"
      role="radio"
      aria-checked={value === f}
      disabled={disabled}
      title={title}
      onClick={() => onChange(f)}
      className={cn(
        "h-10 flex-1 rounded-md px-2 text-xs font-semibold wide:h-7",
        value === f ? "bg-white/15 text-foreground" : "text-muted-foreground hover:text-foreground",
        disabled && "cursor-not-allowed text-muted-foreground/50 hover:text-muted-foreground/50",
      )}
    >
      {label}
    </button>
  );
  return (
    <div role="radiogroup" aria-label="Show material from" className="flex rounded-lg border border-panelline bg-panel p-0.5">
      {option("project", "Project")}
      {option("mine", "Mine")}
      {option("seeds", "Seeds", seedCount === 0, seedCount === 0 ? "The clips of every seed you've been in show up here" : "The clips of every seed you've been in")}
      {option("recent", "Recent", recentCount === 0, recentCount === 0 ? "What you preview or place shows up here" : undefined)}
    </div>
  );
}

function SortMenu({ value, onChange }: { value: MaterialSort; onChange: (s: MaterialSort) => void }) {
  return (
    <label className="relative flex h-9 shrink-0 items-center rounded-lg border border-panelline text-muted-foreground hover:text-foreground wide:h-7" title={`Sort: ${SORT_LABELS[value]}`}>
      <ArrowDownUp className="pointer-events-none absolute left-2 size-3.5" />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as MaterialSort)}
        aria-label="Sort material"
        className="h-full cursor-pointer appearance-none bg-transparent pl-7 pr-2 text-[11px] font-semibold outline-none"
      >
        {(Object.keys(SORT_LABELS) as MaterialSort[]).map((s) => (
          <option key={s} value={s} className="bg-raised text-foreground">
            {SORT_LABELS[s]}
          </option>
        ))}
      </select>
    </label>
  );
}

function EmptyPlace({ place, inFolder = false, page = false }: { place: MaterialPlace; inFolder?: boolean; page?: boolean }) {
  if (place === "mine" && inFolder) {
    return <p className="w-full min-w-0 px-4 py-6 text-center text-xs text-muted-foreground">Nothing here yet — drop audio in, or move clips here.</p>;
  }
  if (place === "project") {
    return <p className="w-full min-w-0 px-4 py-6 text-center text-sm text-muted-foreground">Nothing in this room to build with yet — no track has a clip.</p>;
  }
  if (place === "recent") {
    return <p className="w-full min-w-0 px-4 py-6 text-center text-sm text-muted-foreground">Nothing used yet. What you preview or place shows up here.</p>;
  }
  if (place === "seeds") {
    return <p className="w-full min-w-0 px-4 py-6 text-center text-sm text-muted-foreground">No seeds yet. The clips of every seed you make, jam in or save show up here.</p>;
  }
  return (
    <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-panelline px-4 py-6 text-center">
      <Upload className="size-4 text-muted-foreground" />
      {/* The page has its own Upload, and a drop anywhere on it opens that. */}
      {page ? (
        <p className="text-xs text-muted-foreground">No clips yet. Upload, or drop audio anywhere.</p>
      ) : (
      <p className="text-xs text-muted-foreground">
        No clips yet. Drop audio files here to add them, or{" "}
        <a href="/upload/files" target="_blank" rel="noreferrer" className="underline">
          upload a folder
        </a>
        .
      </p>
      )}
    </div>
  );
}

/** The coarse words a name gives (`drums`, `keys`): matched by `#`, not shown as tags. */
function familyWord(tag: string): boolean {
  return ["drums", "bass", "keys", "vocals", "instruments", "fx"].includes(tag);
}

/** Shared by every row a thumb can hold: no callout, no text selection. */
const TOUCH = "select-none touch-manipulation [-webkit-touch-callout:none] [-webkit-tap-highlight-color:transparent] [-webkit-user-select:none]";

function MaterialRow({
  item,
  active,
  selected,
  playing,
  loading,
  favorite,
  target,
  where,
  inFolder,
  canPlace,
  canDownload,
  canSave,
  removing,
  onSelect,
  onPreview,
  onPlace,
  onFavorite,
  onDownload,
  onSave,
  onMove,
  onDelete,
  onLift,
  onTagTap,
  chosenTags = [],
  suggestedPills = [],
  checkbox = false,
  onRename,
  usesOf,
  onSwap,
  swapName = null,
  player,
  wave = false,
  columns = false,
  quiet = false,
}: {
  item: MaterialItem;
  active: boolean;
  /** One of several rows picked with a modifier key. */
  selected: boolean;
  playing: boolean;
  loading: boolean;
  favorite: boolean;
  target: "timeline" | "pads";
  where: string | null;
  /** A search found it in a subfolder: which one. */
  inFolder: string | null;
  canPlace: boolean;
  canDownload: boolean;
  /** A room MIDI clip that can be kept in the library. */
  canSave: boolean;
  removing: boolean;
  onPreview: () => void;
  onPlace: () => void;
  onFavorite: () => void;
  onDownload: () => void;
  onSave: () => void;
  onMove?: () => void;
  onDelete?: () => void;
  onLift: (lifting: boolean) => void;
  /** A tap on one of the row's tag pills: filter by it. */
  onTagTap?: (tag: string) => void;
  chosenTags?: readonly string[];
  /** Suggestions the search reached this row through, drawn dashed so the
   * reason shows (a tap filters by them like a tag pill). */
  suggestedPills?: readonly string[];
  /** The phone's Select mode: a checkbox on the row, and a tap toggles it. */
  checkbox?: boolean;
  onSelect: (mod: { shift: boolean; toggle: boolean; pure?: boolean }) => void;
  /** A library row: double-click the name to change it. */
  onRename?: (name: string) => void;
  /** A library clip some seed has used: the badge opens the list. */
  usesOf?: () => Promise<ClipUsesResponse>;
  /** The editor holds an audio clip: ⇄ puts this row's sound in its place. */
  onSwap?: () => void;
  swapName?: string | null;
  player: ArrangementPlayerApi;
  /** Draw the clip's waveform beside its art (the first rows only: each one
   * is a fetch of the object). */
  wave?: boolean;
  /** The page's wide layout: length, tempo and added-at as columns. */
  columns?: boolean;
  /** The page: no tag pills on the row, and the buttons wait for a hover or a pick. */
  quiet?: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  // The name as an input while it is being changed; null otherwise.
  const [editing, setEditing] = useState<string | null>(null);
  const [usesOpen, setUsesOpen] = useState(false);
  const [uses, setUses] = useState<ClipUse[] | null>(null);
  const commitRename = (): void => {
    if (editing === null) return;
    const clean = editing.trim();
    if (clean !== "" && clean !== item.name) onRename?.(clean);
    setEditing(null);
  };
  const toggleUses = (): void => {
    setUsesOpen((v) => !v);
    if (uses === null && usesOf) usesOf().then((r) => setUses(r.uses)).catch(() => setUses([]));
  };
  const name = cleanClipName(item.name) || "(unnamed)";
  const color = item.color !== null ? clipColor(item.color, item.trackIndex ?? undefined) : "#ffa529";
  const payload = item.payload;

  // A finger: hold, and the row lifts (lib/lift-drag.ts). Not from a button
  // in the row — pressing ＋ for half a second is not a drag.
  const downAt = useRef<{ id: number; x: number; y: number } | null>(null);
  const cancelLift = useRef<(() => void) | null>(null);
  useEffect(() => () => cancelLift.current?.(), []);
  const hold = useHold((e) => {
    const down = downAt.current;
    if (!e || !down || !payload || "reference" in payload) return;
    onLift(true);
    cancelLift.current = startLift({
      payload,
      label: name,
      color,
      pointerId: down.id,
      clientX: down.x,
      clientY: down.y,
      onEnd: (dropped) => {
        cancelLift.current = null;
        onLift(false);
        if (dropped) track("material_place", { detail: `${item.place}|drag`, value: 1 });
      },
    });
  });
  const press = {
    onPointerDown: (e: ReactPointerEvent) => {
      if ((e.target as HTMLElement).closest("button")) return;
      downAt.current = { id: e.pointerId, x: e.clientX, y: e.clientY };
      hold.onPointerDown(e);
    },
    onPointerMove: (e: ReactPointerEvent) => {
      if (downAt.current) downAt.current = { ...downAt.current, x: e.clientX, y: e.clientY };
      hold.onPointerMove(e);
    },
    onPointerUp: hold.onPointerUp,
    onPointerCancel: hold.onPointerCancel,
    onPointerLeave: hold.onPointerLeave,
    onContextMenu: hold.onContextMenu,
  };

  // Stored tags, two at most, after the facts: what the producer said it is.
  // The row's own tags as pills, three at most, tappable: Live shows an
  // item's tags in the Quick Tags row; here they sit on the row.
  // Stored tags as pills are the shelf's; on the page the inspector (and
  // the Quick Tags under the list) carry them, and the row keeps to its name.
  const pills = item.place === "mine" && !quiet ? sortTags(item.tags.filter((t) => !["loop", "oneshot", "midi"].includes(t) && !familyWord(t))) : [];
  const meta: Array<string | null> = [
    item.seedTitle,
    item.trackName ? cleanTrackName(item.trackName) : null,
    lengthLabel(item),
    item.bpm !== null ? `${item.bpm} BPM` : null,
    item.kind === "oneshot" && item.place === "mine" ? "One-shot" : null,
    item.noteCount !== null ? `${item.noteCount} note${item.noteCount === 1 ? "" : "s"}` : null,
    item.slot !== null ? `scene ${item.slot + 1}` : null,
    inFolder ? `in ${inFolder}` : null,
  ];
  const metaText = meta.filter((m): m is string => m !== null).join(" · ");
  // The page keeps a row quiet: its buttons show on hover, or once it is
  // picked (a tap on a phone) — the name has the row until then.
  const revealed = !quiet || active || selected || confirming || removing;

  return (
    <li
      id={`material-row-${item.key}`}
      data-key={item.key}
      role="option"
      aria-selected={active}
      draggable={payload !== null && !("reference" in payload) && editing === null}
      onDragStart={
        payload
          ? (e) => {
              if ("reference" in payload) return;
              if ("material" in payload) e.dataTransfer.setData(MATERIAL_DRAG_TYPE, payload.material);
              else if ("mineMidi" in payload) e.dataTransfer.setData(MINE_MIDI_DRAG_TYPE, payload.mineMidi);
              else e.dataTransfer.setData(MINE_DRAG_TYPE, payload.mine);
              e.dataTransfer.effectAllowed = "copyMove";
            }
          : undefined
      }
      onClick={(e) => {
        if (hold.consumeHold()) return;
        if (checkbox) onSelect({ shift: false, toggle: true, pure: true });
        else onSelect({ shift: e.shiftKey, toggle: e.metaKey || e.ctrlKey });
      }}
      {...press}
      title={
        payload
          ? "reference" in payload
            ? `${name} — ＋ keeps it in Mine and places it`
            : `${name} — drag onto a ${item.kind === "midi" ? "MIDI" : "audio"} ${target === "pads" ? "pad" : "track"}, or hold to carry it`
          : item.place === "seeds"
            ? `${name} — can be heard, not kept: its audio predates the shared pool`
            : `${name} — nothing to place: this clip has no object of its own`
      }
      className={cn(
        TOUCH,
        "group flex min-h-11 items-center gap-2 rounded-lg border px-2 py-1 transition-colors wide:min-h-9",
        payload ? "cursor-grab border-panelline bg-panel hover:bg-white/[0.04] active:cursor-grabbing" : "border-dashed border-panelline bg-panel/50 opacity-60",
        playing && "border-primary",
        active && "ring-1 ring-foreground/40",
        selected && "bg-primary/10 ring-1 ring-primary/50",
      )}
    >
      {checkbox && (
        <span
          role="checkbox"
          aria-checked={selected}
          aria-label={`Select ${name}`}
          className={cn("flex size-5 shrink-0 items-center justify-center rounded border", selected ? "border-primary bg-primary text-primary-foreground" : "border-controlline bg-control")}
        >
          {selected && <Check className="size-3.5" />}
        </span>
      )}
      {/* The art is the preview: a thumb's biggest target on the row. */}
      <button
        type="button"
        onClick={onPreview}
        disabled={item.playable === null}
        title={item.playable !== null ? (playing ? "Stop" : "Preview") : "Nothing to preview"}
        aria-label={item.playable !== null ? `${playing ? "Stop" : "Preview"} ${name}` : undefined}
        className={cn(
          "relative flex size-8 shrink-0 items-center justify-center rounded-md border text-black/80 wide:size-7",
          item.playable !== null ? "hover:opacity-80" : "cursor-not-allowed opacity-40",
        )}
        style={{ backgroundColor: color, borderColor: "rgba(0,0,0,.35)" }}
      >
        {loading ? <Loader2 className="size-3.5 animate-spin" /> : playing ? <Pause className="size-3.5" /> : item.kind === "midi" ? <Music className="size-3.5" /> : <Play className="size-3.5" />}
      </button>
      {wave && item.kind !== "midi" && item.playable !== null && !("notes" in item.playable) && <RowWave player={player} src={item.playable} color={color} />}

      <div className={cn("min-w-0 flex-1", columns && "lg:grid lg:grid-cols-[minmax(0,1fr)_5.5rem_5rem_6.5rem] lg:items-center lg:gap-x-3")}>
        {columns && (
          <>
            <span className="hidden truncate text-[11px] text-muted-foreground lg:col-start-2 lg:row-start-1 lg:block">{lengthLabel(item)}</span>
            <span className="hidden truncate text-[11px] text-muted-foreground lg:col-start-3 lg:row-start-1 lg:block">{item.bpm !== null ? `${item.bpm} BPM` : item.kind === "oneshot" ? "One-shot" : item.kind === "midi" ? "MIDI" : "—"}</span>
            <span className="hidden truncate text-[11px] text-muted-foreground lg:col-start-4 lg:row-start-1 lg:block" title={item.createdAt ? new Date(item.createdAt).toLocaleString() : undefined}>
              {item.createdAt ? relativeTime(item.createdAt) : ""}
            </span>
          </>
        )}
        {editing !== null ? (
          <input
            autoFocus
            value={editing}
            aria-label={`Rename ${name}`}
            onChange={(e) => setEditing(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onBlur={commitRename}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") commitRename();
              else if (e.key === "Escape") setEditing(null);
            }}
            className="h-6 w-full rounded border border-controlline bg-control px-1.5 text-xs font-semibold outline-none focus:border-primary"
          />
        ) : (
          <p
            className="flex items-center gap-1.5 text-xs font-semibold"
            title={onRename ? `${item.name} — double-click to rename` : item.name}
            onDoubleClick={
              onRename
                ? (e) => {
                    e.stopPropagation();
                    setEditing(item.name);
                  }
                : undefined
            }
          >
            {item.swatch !== null && <span className="inline-block size-2 shrink-0 rounded-full" style={{ backgroundColor: SWATCHES[item.swatch] ?? SWATCHES[0] }} aria-label={`Colour ${item.swatch + 1}`} />}
            <span className="min-w-0 flex-1 truncate">{name}</span>
            {usesOf ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleUses();
                }}
                aria-expanded={usesOpen}
                title={usesOpen ? "Hide where it is used" : `Used in ${item.uses} seed${item.uses === 1 ? "" : "s"} — show them`}
                className={cn("shrink-0 rounded border px-1 font-mono text-[10px] font-normal", usesOpen ? "border-primary/60 text-foreground" : "border-panelline text-muted-foreground hover:text-foreground")}
              >
                used ×{item.uses}
              </button>
            ) : (
              item.uses > 1 && (
                <span className="shrink-0 rounded border border-panelline px-1 font-mono text-[10px] font-normal text-muted-foreground" title={`Placed ${item.uses} times`}>
                  ×{item.uses}
                </span>
              )
            )}
            {/* The facts, after the name on the same line — the name gets the room, the facts a corner. */}
            {metaText && (
              <span className={cn("max-w-[45%] shrink-0 truncate text-[10px] font-normal text-muted-foreground", columns && "lg:hidden", quiet && !columns && "group-hover:hidden group-focus-within:hidden", revealed && !columns && "hidden")} title={metaText}>
                {metaText}
              </span>
            )}
          </p>
        )}
        {usesOpen && (
          <p className="flex flex-wrap items-center gap-x-2 text-[10px] text-muted-foreground" aria-label="Used in">
            {uses === null ? (
              <Loader2 className="size-2.5 animate-spin" />
            ) : uses.length === 0 ? (
              "Not placed in a seed yet."
            ) : (
              uses.map((u) => (
                <Link key={u.seedId} to={seedPath(u.seedId)} onClick={(e) => e.stopPropagation()} className="truncate text-primary/90 hover:underline" title={u.derived ? "Placed from a copy of this clip" : "Placed from this clip"}>
                  {u.title}
                </Link>
              ))
            )}
          </p>
        )}
        {(pills.length > 0 || suggestedPills.length > 0) && (
          <p className="mt-0.5 flex flex-wrap items-center gap-1" aria-label="Tags">
            {pills.slice(0, 3).map((t) => {
              const on = chosenTags.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onTagTap?.(t);
                  }}
                  title={on ? `Stop filtering by ${tagLabel(t)}` : `Only ${tagLabel(t)}`}
                  className={cn(
                    "h-5 rounded px-1.5 text-[10px] leading-5",
                    on ? "bg-white/20 text-foreground" : "bg-white/[0.07] text-muted-foreground hover:bg-white/15 hover:text-foreground",
                  )}
                >
                  {tagLabel(t)}
                </button>
              );
            })}
            {pills.length > 3 && <span className="text-[10px] text-muted-foreground">+{pills.length - 3}</span>}
            {suggestedPills.slice(0, 2).map((t) => (
              <button
                key={`s:${t}`}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onTagTap?.(t);
                }}
                title={`Suggested ${tagLabel(t)} — not kept yet`}
                className="h-5 rounded border border-dashed border-primary/50 px-1.5 text-[10px] leading-4 text-muted-foreground hover:text-foreground"
              >
                {tagLabel(t)}
              </button>
            ))}
            {suggestedPills.length > 2 && <span className="text-[10px] text-muted-foreground">+{suggestedPills.length - 2}</span>}
          </p>
        )}
        {where && canPlace && (
          <p className="truncate text-[10px] text-primary/90" title={`＋ puts it ${where}`}>
            → {where}
          </p>
        )}
      </div>

      {/* A set star stays in view; the rest of the buttons wait for a hover or a pick. */}
      {favorite && !revealed && (
        <button type="button" onClick={onFavorite} aria-pressed aria-label={`Unstar ${name}`} title="Starred" className="flex size-8 shrink-0 items-center justify-center rounded-md text-primary group-hover:hidden group-focus-within:hidden wide:size-7">
          <Star className="size-3.5 fill-current" />
        </button>
      )}
      <span className={cn("flex shrink-0 items-center gap-0.5", !revealed && "hidden group-focus-within:flex group-hover:flex")}>
      <button
        type="button"
        onClick={onFavorite}
        aria-pressed={favorite}
        aria-label={favorite ? `Unstar ${name}` : `Star ${name}`}
        title={favorite ? "Starred" : "Star"}
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-md wide:size-7",
          favorite ? "text-primary" : "text-muted-foreground/60 hover:bg-white/10 hover:text-foreground",
        )}
      >
        <Star className={cn("size-3.5", favorite && "fill-current")} />
      </button>

      {confirming ? (
        <span className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => {
              setConfirming(false);
              onDelete?.();
            }}
            className="h-8 rounded-md bg-removed/20 px-2 text-[11px] font-semibold text-removed hover:bg-removed/30"
          >
            Delete
          </button>
          <button type="button" onClick={() => setConfirming(false)} className="h-8 rounded-md px-2 text-[11px] text-muted-foreground hover:text-foreground">
            Keep
          </button>
        </span>
      ) : removing ? (
        <Loader2 className="size-3.5 shrink-0 animate-spin text-muted-foreground" />
      ) : (
        <>
          {onDelete && (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              title="Delete from your library (projects that use it keep it)"
              aria-label={`Delete ${name}`}
              className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-white/10 hover:text-foreground wide:size-7"
            >
              <Trash2 className="size-3.5" />
            </button>
          )}
          {onMove && (
            <button
              type="button"
              onClick={onMove}
              title="Move to a folder"
              aria-label={`Move ${name} to a folder`}
              className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-white/10 hover:text-foreground wide:size-7"
            >
              <FolderInput className="size-3.5" />
            </button>
          )}
          {onSwap && (
            <button
              type="button"
              onClick={onSwap}
              title={`Swap this in for ${swapName || "the clip being edited"} — it keeps its place`}
              aria-label={`Swap ${name} in for ${swapName || "the clip being edited"}`}
              className="flex size-8 shrink-0 items-center justify-center rounded-md border border-primary/40 text-primary hover:bg-primary/15 wide:size-7"
            >
              <ArrowLeftRight className="size-3.5" />
            </button>
          )}
          {canSave && (
            <button
              type="button"
              onClick={onSave}
              title={item.place === "seeds" ? "Keep in Mine (your library)" : "Save to Mine (your library)"}
              aria-label={`Save ${name} to Mine`}
              className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-white/10 hover:text-foreground wide:size-7"
            >
              <Save className="size-3.5" />
            </button>
          )}
          {canDownload && (
            <button
              type="button"
              onClick={onDownload}
              title={`Download ${name}`}
              aria-label={`Download ${name}`}
              className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-white/10 hover:text-foreground wide:size-7"
            >
              <Download className="size-3.5" />
            </button>
          )}
        </>
      )}
      </span>
      {/* The way in that needs no aim: always in view, sized for a thumb, trimmed once there is a mouse. */}
      {canPlace && !confirming && !removing && (
        <button
          type="button"
          onClick={onPlace}
          title={where ? `Put ${name} ${where}` : target === "pads" ? `Put ${name} in a pad` : `Place ${name} at the playhead`}
          aria-label={where ? `Put ${name} ${where}` : target === "pads" ? `Put ${name} in a pad` : `Place ${name} at the playhead`}
          className="flex size-8 shrink-0 items-center justify-center rounded-md border border-controlline bg-control text-foreground/80 hover:bg-white/10 hover:text-foreground wide:size-7"
        >
          <Plus className="size-4" />
        </button>
      )}
    </li>
  );
}

/** Live's Collections: seven colours a row (or many) can carry. */
export const SWATCHES = ["#ff5a5a", "#ffa529", "#ffe15a", "#5ad27a", "#4fc3f7", "#9b7bff", "#ff7ad9"] as const;

/** The library clip id in a payload, when it is one. */
function mineClipId(payload: MaterialPayload | null): string | null {
  return payload !== null && "mine" in payload ? payload.mine : null;
}

function formatMb(b: number): string {
  return b >= 1024 ** 3 ? `${(b / 1024 ** 3).toFixed(1)} GB` : `${Math.max(1, Math.round(b / 1024 ** 2))} MB`;
}

/** How many rows draw a waveform: each is a fetch of its object. */
const WAVE_ROWS = 40;
const WAVE_W = 48;
const WAVE_H = 16;

/** The clip's waveform beside its art, drawn once its object has been read.
 * Asked for at `later` priority, behind whatever the room is drawing. */
function RowWave({ player, src, color }: { player: ArrangementPlayerApi; src: ClipSource; color: string }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const { ensurePeaks, peaks, decodedVersion } = player;
  useEffect(() => {
    ensurePeaks(src.url, "later");
  }, [ensurePeaks, src.url]);
  const data = useMemo(() => {
    void decodedVersion;
    return peaks(src.url, WAVE_W * 2, src.startSec, src.durationSec);
  }, [peaks, decodedVersion, src.url, src.startSec, src.durationSec]);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !data) return;
    const g = prepareCanvas(canvas, WAVE_W, WAVE_H);
    if (g) drawEnvelope(g, data, WAVE_W, WAVE_H, `${color}cc`);
  }, [data, color]);
  return (
    <span className="hidden h-4 w-12 shrink-0 sm:block" aria-hidden>
      {data && <canvas ref={ref} style={{ width: WAVE_W, height: WAVE_H }} />}
    </span>
  );
}

/** Undo the last drop: one tap asks, the second removes every clip it added. */
function UndoBatch({ count, onUndo }: { count: number; onUndo: () => void }) {
  const [asking, setAsking] = useState(false);
  const [done, setDone] = useState(false);
  if (done) return null;
  return asking ? (
    <span className="flex items-center gap-1">
      Remove {count === 1 ? "it" : `all ${count}`}?
      <button
        type="button"
        onClick={() => {
          onUndo();
          setDone(true);
          track("library_batch_undone", { value: count });
        }}
        className="h-6 rounded-md bg-removed/20 px-2 font-semibold text-removed hover:bg-removed/30"
      >
        Remove
      </button>
      <button type="button" onClick={() => setAsking(false)} className="h-6 rounded-md px-2 hover:text-foreground">
        Keep
      </button>
    </span>
  ) : (
    <button type="button" onClick={() => setAsking(true)} className="underline hover:text-foreground" title="Take this drop back out of the library">
      Undo
    </button>
  );
}

/** Rows rendered per page of a long list. */
const ROW_PAGE = 150;
/** The list width the page's columns need: the name keeps half the row. */
const COLUMNS_MIN_PX = 640;

/** The inspector's waveform: the clip, full width, in its colour. */
const INSPECTOR_W = 268;
const INSPECTOR_H = 72;
function InspectorWave({ player, src, color }: { player: ArrangementPlayerApi; src: ClipSource; color: string }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const { ensurePeaks, peaks, decodedVersion } = player;
  useEffect(() => {
    ensurePeaks(src.url, "now");
  }, [ensurePeaks, src.url]);
  const data = useMemo(() => {
    void decodedVersion;
    return peaks(src.url, INSPECTOR_W * 2, src.startSec, src.durationSec);
  }, [peaks, decodedVersion, src.url, src.startSec, src.durationSec]);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !data) return;
    const g = prepareCanvas(canvas, INSPECTOR_W, INSPECTOR_H);
    if (g) drawEnvelope(g, data, INSPECTOR_W, INSPECTOR_H, `${color}d9`);
  }, [data, color]);
  return (
    <div className="flex h-[72px] w-full items-center justify-center overflow-hidden rounded-lg border border-panelline bg-sunk" aria-hidden>
      {data ? <canvas ref={ref} style={{ width: INSPECTOR_W, height: INSPECTOR_H }} /> : <Loader2 className="size-4 animate-spin text-muted-foreground" />}
    </div>
  );
}

/**
 * The page's inspector: what is selected, large — its waveform, name,
 * facts, the things that can be done to it, its colour and its tags — in a
 * column of its own that is there before anything is picked, as the sample
 * managers do it (Live 12.2's Quick Tags panel, Komplete Kontrol's Info
 * pane, Loopcloud's Properties, Sononym's metadata panel). Several rows
 * selected: the count, what they share, and the union of suggestions.
 */
function Inspector({
  targets,
  total,
  selecting,
  signedIn,
  player,
  folderName,
  confirmingDelete,
  starredAll,
  canDownload,
  complete,
  onSet,
  onUnset,
  onAcceptTempo,
  onReview,
  swatch,
  onColor,
  onRename,
  onSelectAll,
  onClear,
  onDone,
  onMove,
  onStar,
  onDownload,
  onDelete,
  onDeleteConfirm,
  onDeleteCancel,
}: {
  targets: MaterialItem[];
  total: number;
  selecting: boolean;
  signedIn: boolean;
  player: ArrangementPlayerApi;
  folderName: ReadonlyMap<string, string>;
  confirmingDelete: boolean;
  starredAll: boolean;
  canDownload: boolean;
  complete: (prefix: string) => TagCompletion[];
  onSet: (tags: string[]) => void;
  onUnset: (tags: string[]) => void;
  onAcceptTempo: (bpm: number) => void;
  onReview: (keep: boolean, tags?: readonly string[]) => void;
  swatch: number | null;
  onColor: (value: number | null) => void;
  onRename: (name: string) => void;
  onSelectAll: () => void;
  onClear: () => void;
  onDone: () => void;
  onMove: () => void;
  onStar: (starred: boolean) => void;
  onDownload: () => void;
  onDelete: () => void;
  onDeleteConfirm: () => void;
  onDeleteCancel: () => void;
}) {
  const one = targets.length === 1 ? targets[0]! : null;
  const [editing, setEditing] = useState<string | null>(null);
  useEffect(() => setEditing(null), [one?.key]);
  const action = "flex h-8 items-center gap-1.5 rounded-md border border-panelline px-2 text-[11px] font-semibold text-muted-foreground hover:bg-white/10 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40";
  const color = one ? (one.color !== null ? clipColor(one.color, one.trackIndex ?? undefined) : one.swatch !== null ? (SWATCHES[one.swatch] ?? "#ffa529") : "#ffa529") : "#ffa529";
  const kinds = new Set(targets.map((t) => t.kind));
  const facts: Array<[string, string]> = one
    ? [
        ["Length", lengthLabel(one)],
        [one.kind === "midi" ? "Notes" : "Tempo", one.kind === "midi" ? String(one.noteCount ?? 0) : one.bpm !== null ? `${one.bpm} BPM` : one.kind === "oneshot" ? "One-shot" : "—"],
        ["Added", one.createdAt ? relativeTime(one.createdAt) : "—"],
        ...(one.bytes > 0 ? [["Size", formatMb(one.bytes)] as [string, string]] : []),
        ["Folder", one.folderId ? (folderName.get(one.folderId) ?? "—") : "Mine"],
        ["Used in", one.uses === 0 ? "no seed yet" : `${one.uses} seed${one.uses === 1 ? "" : "s"}`],
      ]
    : [];
  // Nothing picked: a quiet hint, not a panel of instructions.
  if (targets.length === 0) {
    return signedIn ? (
      <div className="flex items-center justify-center gap-2 rounded-2xl border border-dashed border-hairline px-4 py-6 text-[11px] text-muted-foreground" aria-label="Inspector">
        <Tag className="size-3.5" /> Select a clip to see it here
      </div>
    ) : null;
  }
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-hairline bg-raised p-3 text-xs" aria-label="Inspector">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{targets.length > 1 ? `${targets.length} selected` : "Inspector"}</span>
        {targets.length > 1 && total > 0 && targets.length === total && <span className="text-[10px] text-muted-foreground">(all shown)</span>}
        <span className="ml-auto" />
        {targets.length > 0 && targets.length < total && (
          <button type="button" onClick={onSelectAll} className="text-[11px] text-muted-foreground underline hover:text-foreground" title="Every row shown (⌘A)">
            Select all {total}
          </button>
        )}
        {targets.length > 0 && (
          <button type="button" onClick={selecting ? onDone : onClear} aria-label="Clear the selection" className="flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-white/10 hover:text-foreground">
            <X className="size-3.5" />
          </button>
        )}
      </div>

      {targets.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-8 text-center text-muted-foreground">
          <Tag className="size-5" />
          <p className="text-xs">{signedIn ? "Pick a clip to hear it large, tag it and file it." : "Sign in to tag and file your clips."}</p>
          {signedIn && (
            <p className="text-[10px] leading-5">
              ⇧-click a range · ⌘-click to add one · ⌘A for all shown
              <br />
              ⌘E adds a tag · double-click a name to rename it
            </p>
          )}
        </div>
      ) : (
        <>
          {one ? (
            one.kind !== "midi" && one.playable !== null && !("notes" in one.playable) ? (
              <InspectorWave player={player} src={one.playable} color={color} />
            ) : (
              <div className="flex h-[72px] w-full items-center justify-center gap-2 rounded-lg border border-panelline bg-sunk text-muted-foreground" aria-hidden>
                <Music className="size-4" /> {one.noteCount ?? 0} notes
              </div>
            )
          ) : (
            <div className="flex h-[72px] w-full items-center justify-center rounded-lg border border-panelline bg-sunk text-muted-foreground" aria-hidden>
              {[...kinds].map((k) => (k === "midi" ? "MIDI" : k === "loop" ? "loops" : "one-shots")).join(" · ")}
            </div>
          )}

          {one &&
            (editing !== null ? (
              <input
                autoFocus
                value={editing}
                aria-label={`Rename ${cleanClipName(one.name) || "(unnamed)"}`}
                onChange={(e) => setEditing(e.target.value)}
                onBlur={() => {
                  const clean = editing.trim();
                  if (clean !== "" && clean !== one.name) onRename(clean);
                  setEditing(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                  else if (e.key === "Escape") setEditing(null);
                }}
                className="h-8 w-full rounded-md border border-controlline bg-control px-2 text-sm font-semibold outline-none focus:border-primary"
              />
            ) : (
              <button type="button" onClick={() => setEditing(one.name)} title="Rename" className="min-w-0 truncate text-left text-sm font-semibold hover:underline">
                {cleanClipName(one.name) || "(unnamed)"}
              </button>
            ))}

          {facts.length > 0 && (
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px]">
              {facts.map(([k, v]) => (
                <Fragment key={k}>
                  <dt className="text-muted-foreground">{k}</dt>
                  <dd className="truncate">{v}</dd>
                </Fragment>
              ))}
            </dl>
          )}

          {confirmingDelete ? (
            <div className="flex items-center gap-1 text-[11px]">
              <span className="text-muted-foreground">Projects that use {targets.length === 1 ? "it" : "them"} keep {targets.length === 1 ? "it" : "them"}.</span>
              <button type="button" onClick={onDeleteConfirm} className="ml-auto h-8 rounded-md bg-removed/20 px-2 font-semibold text-removed hover:bg-removed/30">
                Delete {targets.length}
              </button>
              <button type="button" onClick={onDeleteCancel} className="h-8 rounded-md px-2 text-muted-foreground hover:text-foreground">
                Keep
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-1" role="toolbar" aria-label="Actions">
              <button type="button" onClick={() => onStar(!starredAll)} className={action} title={starredAll ? "Unstar" : "Star"}>
                <Star className={cn("size-3.5", starredAll && "fill-current text-primary")} /> {starredAll ? "Unstar" : "Star"}
              </button>
              <button type="button" onClick={onMove} className={action} title="Move to a folder">
                <FolderInput className="size-3.5" /> Move to…
              </button>
              {one && canDownload && one.kind !== "midi" && (
                <button type="button" onClick={onDownload} className={action} title="Save the file to disk">
                  <Download className="size-3.5" /> Download
                </button>
              )}
              <button type="button" onClick={onDelete} className={action} title="Delete from your library">
                <Trash2 className="size-3.5" /> Delete
              </button>
            </div>
          )}

          <QuickTags
            layout="panel"
            targets={targets}
            complete={complete}
            onSet={onSet}
            onUnset={onUnset}
            onAcceptTempo={onAcceptTempo}
            onReview={onReview}
            swatch={swatch}
            onColor={onColor}
            onClear={onClear}
          />
        </>
      )}
    </div>
  );
}

/** Whether a chip means anything in a place: kinds and ★ everywhere, a track
 * only in Project, a seed only in Seeds, tags and the review chips where
 * there are library rows (Mine, Recent). */
function chipBelongs(chip: MaterialChip, place: MaterialPlace): boolean {
  if (chip === "loop" || chip === "oneshot" || chip === "midi" || chip === "favorite") return true;
  if (chip.startsWith("track:")) return place === "project";
  if (chip.startsWith("seed:")) return place === "seeds";
  return place === "mine" || place === "recent";
}
